import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  Grid,
  Card,
  CardContent,
  Autocomplete,
  CircularProgress,
  FormControlLabel,
  Switch,
  Avatar,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  List as ListIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';

import productService from '../../services/productService';
import salesService from '../../services/salesService';
import { customerService } from '../../services/customerService';
import axiosInstance from '../../utils/axios';
import { useNavigate } from 'react-router-dom';
import companySettingsService from '../../services/companySettingsService';
import printSettingsService from '../../services/printSettingsService';
import { shareBillToWhatsApp } from '../../utils/whatsappShareHelper';
import unitService from '../../services/unitService';
import { getCurrentUser } from '../../services/authService';
import CustomerForm from './CustomerForm';
import { PersonAdd as PersonAddIcon } from '@mui/icons-material';
import HoldBillModal from './HoldBillModal'; // New import
import ViewHoldBillsModal from './ViewHoldBillsModal'; // New import
import { holdBillService } from '../../services/holdBillService'; // New import
import salesmanService from '../../services/salesmanService';
import loyaltySettingsService from '../../services/loyaltySettingsService';
import SplitBillDialog from './SplitBillDialog';
import { generateOfflineReceiptPdf } from '../../utils/offlineReceiptGenerator';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '8px 12px',
  border: '1px solid #e0e0e0',
  fontSize: '15px',
  '&.focused': {
    backgroundColor: '#e3f2fd',
    outline: `2px solid ${theme.palette.primary.main}`,
  },
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
  padding: '10px 12px',
  border: '1px solid #bdc3c7',
  backgroundColor: '#2c3e50',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '15px',
  textAlign: 'center',
}));

const FunctionKeyBar = styled(Box)(({ theme }) => ({
  backgroundColor: '#ffffff',
  borderTop: '2px solid #3498db',
  padding: '12px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '8px',
}));

const FunctionKey = styled(Button)(({ theme }) => ({
  minWidth: '100px',
  padding: '8px 12px',
  backgroundColor: '#ecf0f1',
  color: '#2c3e50',
  fontSize: '13px',
  fontWeight: '600',
  textTransform: 'none',
  border: '1px solid #bdc3c7',
  '&:hover': {
    backgroundColor: '#bdc3c7',
  },
  '& .key': {
    fontWeight: 'bold',
    color: '#3498db',
    fontSize: '14px',
    marginRight: '4px',
  },
}));

const PAGE_KEY_POS_SALES_KEYBOARD = 'POS_SALES_KEYBOARD';

const POSKeyboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Refs for input cells
  const itemCodeRefs = useRef([]);
  const qtyRefs = useRef([]);
  const priceRefs = useRef([]);
  const discountRefs = useRef([]);
  const uomRefs = useRef([]);
  const deleteNoRef = useRef(null);
  const deleteYesRef = useRef(null);
  const handleTabOrderEnterRef = useRef(null);

  // Page field visibility and tab order (POS Sales Keyboard)
  const [pageFieldConfig, setPageFieldConfig] = useState({
    mrp: true,
    gst: true,
    uom: true,
    discount: true
  });
  const [tabOrderConfig, setTabOrderConfig] = useState({});
  const [roundOffEnabled, setRoundOffEnabled] = useState(false);
  const [roundOffMode, setRoundOffMode] = useState('NEAREST'); // DOWN | UP | NEAREST

  // State
  const [cart, setCart] = useState([
    { id: 1, itemCode: '', productName: '', quantity: 1, price: 0, discount: 0, discountPercentage: 0, discountAmount: 0, product: null, selectedUnit: null },
  ]);
  const [customer, setCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerInputValue, setCustomerInputValue] = useState('');
  const [newCustomerName, setNewCustomerName] = useState('');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [splitBillDialogOpen, setSplitBillDialogOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [receivedAmount, setReceivedAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [discount, setDiscount] = useState(0);
  const [overallDiscountPercentage, setOverallDiscountPercentage] = useState(0);
  const [discountType, setDiscountType] = useState('AMOUNT');
  const [searchingProduct, setSearchingProduct] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [productOptions, setProductOptions] = useState({});
  const autocompleteRef = useRef(null);
  const [enableWhatsappShare, setEnableWhatsappShare] = useState(false);
  const [directPrint, setDirectPrint] = useState(false);
  const [previewPrint, setPreviewPrint] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [previewPrintDialogOpen, setPreviewPrintDialogOpen] = useState(false);
  const [printChoiceDialogOpen, setPrintChoiceDialogOpen] = useState(false);
  const [lastCreatedOrder, setLastCreatedOrder] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [units, setUnits] = useState([]);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);

  // Salesman (optional - for attributing bill to a salesman; no login)
  const [salesmen, setSalesmen] = useState([]);
  const [selectedSalesmanId, setSelectedSalesmanId] = useState('');

  // Loyalty: settings (point value), redeemed points on this bill
  const [loyaltySettings, setLoyaltySettings] = useState(null);
  const [redeemedPoints, setRedeemedPoints] = useState(0);

  // Non-GST mode: Sale Rate shows tax-included rate, GST column shows 0, total same (Ctrl+Q to toggle)
  const [nonGstMode, setNonGstMode] = useState(false);

  // Quotation / edit mode state
  const [isQuotationMode, setIsQuotationMode] = useState(false);
  const [quotationId, setQuotationId] = useState(null); // Store quotation ID when loading from View Orders
  const [editOrderId, setEditOrderId] = useState(null); // Store existing order ID when editing a bill
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [purchasePriceDialogOpen, setPurchasePriceDialogOpen] = useState(false);
  const [purchasePriceDialogData, setPurchasePriceDialogData] = useState(null); // { productName, purchasePrice }
  const [holdBillModalOpen, setHoldBillModalOpen] = useState(false);
  const [viewHoldBillsModalOpen, setViewHoldBillsModalOpen] = useState(false);
  const [user, setUser] = useState(getCurrentUser());
  const [recentOrders, setRecentOrders] = useState([]);

  // Price mode: one rate type for the whole transaction (retailer or wholesale)
  const [priceMode, setPriceMode] = useState('retailer'); // 'retailer' | 'wholesale'

  // Clock state for real-time display in top bar
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load page field visibility and tab order config
  // Note: getPageFieldConfig returns the fieldConfig object (e.g. { mrp, gst, uom, discount, focusOrder })
  useEffect(() => {
    let cancelled = false;
    companySettingsService
      .getPageFieldConfig(PAGE_KEY_POS_SALES_KEYBOARD)
      .then((fieldConfig) => {
        if (!cancelled && fieldConfig && typeof fieldConfig === 'object') {
          setPageFieldConfig((prev) => ({
            mrp: fieldConfig.mrp !== false,
            gst: fieldConfig.gst !== false,
            uom: fieldConfig.uom !== false,
            discount: fieldConfig.discount !== false
          }));
          const focusOrder = fieldConfig.focusOrder;
          setTabOrderConfig(focusOrder && typeof focusOrder === 'object' ? focusOrder : {});
          setRoundOffEnabled(Boolean(fieldConfig.roundOffEnabled));
          setRoundOffMode(fieldConfig.roundOffMode || 'NEAREST');
        }
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  // Listen for company information updates
  useEffect(() => {
    const handleCompanyUpdate = () => {
      setUser(getCurrentUser());
    };

    window.addEventListener('companyInfoUpdated', handleCompanyUpdate);
    window.addEventListener('storage', handleCompanyUpdate);

    return () => {
      window.removeEventListener('companyInfoUpdated', handleCompanyUpdate);
      window.removeEventListener('storage', handleCompanyUpdate);
    };
  }, []);

  // When price mode changes, update all cart item prices to the correct rate for their selected unit
  useEffect(() => {
    setCart(prev => prev.map(item => {
      if (!item.product) return item;
      const availableUnits = getAvailableUnits(item.product);
      const selectedUnitData = item.selectedUnit
        ? availableUnits.find(u => u.id === item.selectedUnit?.id)
        : availableUnits[0];
      const newPrice = selectedUnitData ? selectedUnitData.price : getSaleRateForMode(item.product);
      return { ...item, price: newPrice };
    }));
  }, [priceMode]); // eslint-disable-line react-hooks/exhaustive-deps -- only run when priceMode toggles; getAvailableUnits/getSaleRateForMode are stable for that

  // Derived visibility flags for data table columns only. Item Code & Product Name are fixed.
  const showMrp = pageFieldConfig.mrp !== false;
  const showGst = pageFieldConfig.gst !== false;
  const showUom = pageFieldConfig.uom !== false;
  const showDiscount = pageFieldConfig.discount !== false;

  // Tab order: Qty → Sale Rate → Discount → new row. UOM is never in tab order (user can change with mouse if needed).
  const orderedFieldKeys = React.useMemo(() => {
    const config = tabOrderConfig || {};
    const keys = ['itemCode', 'qty', 'saleRate', 'discount'];
    const withOrder = keys
      .filter((k) => (k !== 'discount' || showDiscount))
      .map((k) => ({ key: k, order: config[k] != null && config[k] !== '' ? Number(config[k]) : null }))
      .filter((x) => x.order != null && x.order > 0);
    if (withOrder.length === 0) return ['itemCode', 'qty', 'saleRate', ...(showDiscount ? ['discount'] : [])];
    withOrder.sort((a, b) => a.order - b.order);
    return withOrder.map((x) => x.key);
  }, [tabOrderConfig, showDiscount]);

  const focusRef = useCallback((key, rowIndex) => {
    const r = key === 'itemCode' ? itemCodeRefs.current[rowIndex]
      : key === 'qty' ? qtyRefs.current[rowIndex]
        : key === 'uom' ? uomRefs.current[rowIndex]
          : key === 'saleRate' ? priceRefs.current[rowIndex]
            : key === 'discount' ? discountRefs.current[rowIndex]
              : null;
    if (r) {
      r.focus?.();
      if (r.select) r.select();
    }
  }, []);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  // Load recent orders
  const loadRecentOrders = useCallback(async () => {
    try {
      const response = await salesService.getAllSalesOrders({ page: 0, size: 3, sort: 'createdAt', direction: 'DESC' });
      const orders = response.content || [];
      setRecentOrders(orders);
    } catch (error) {
      console.error('Failed to load recent orders', error);
    }
  }, []);

  // Helper to get customer's due/credit balance (consistent with POS screen)
  const getCustomerCreditBalance = (cust) => {
    if (!cust) return 0;
    const creditValue = cust.creditBalance || cust.credit_balance || cust.credit || 0;
    return Number(creditValue) || 0;
  };

  // Calculate totals
  const calculateTotals = useCallback(() => {
    const itemDiscounts = cart.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);

    if (nonGstMode) {
      // Non-GST: cart prices are tax-inclusive; display tax as 0
      const subtotal = cart.reduce((sum, item) => {
        if (!item.product) return sum;
        return sum + (item.price * item.quantity) - (Number(item.discount) || 0);
      }, 0);
      const taxAmount = 0;
      const rawTotal = subtotal - discount;

      let finalTotal = rawTotal;
      let roundOff = 0;
      if (roundOffEnabled) {
        const rupees = Math.floor(rawTotal);
        const paise = rawTotal - rupees;
        if (roundOffMode === 'DOWN') {
          finalTotal = Math.floor(rawTotal);
        } else if (roundOffMode === 'UP') {
          finalTotal = Math.ceil(rawTotal);
        } else {
          // NEAREST: below .50 -> down, .50 and above -> up
          finalTotal = paise >= 0.5 ? Math.ceil(rawTotal) : Math.floor(rawTotal);
        }
        roundOff = finalTotal - rawTotal;
      }

      return { subtotal, taxAmount, total: finalTotal, discount: discount + itemDiscounts, roundOff };
    }

    const subtotal = cart.reduce((sum, item) => {
      if (!item.product) return sum;
      return sum + (item.price * item.quantity);
    }, 0);

    const taxAmount = cart.reduce((sum, item) => {
      if (!item.product) return sum;
      const itemTotal = (item.price * item.quantity) - (item.discount || 0);
      const taxRate = (item.product?.tax?.rate || 0) / 100;
      return sum + (itemTotal * taxRate);
    }, 0);

    const rawTotal = subtotal + taxAmount - itemDiscounts - discount;

    let finalTotal = rawTotal;
    let roundOff = 0;
    if (roundOffEnabled) {
      const rupees = Math.floor(rawTotal);
      const paise = rawTotal - rupees;
      if (roundOffMode === 'DOWN') {
        finalTotal = Math.floor(rawTotal);
      } else if (roundOffMode === 'UP') {
        finalTotal = Math.ceil(rawTotal);
      } else {
        // NEAREST: below .50 -> down, .50 and above -> up
        finalTotal = paise >= 0.5 ? Math.ceil(rawTotal) : Math.floor(rawTotal);
      }
      roundOff = finalTotal - rawTotal;
    }

    return { subtotal, taxAmount, total: finalTotal, discount: discount + itemDiscounts, roundOff };
  }, [cart, discount, nonGstMode, roundOffEnabled, roundOffMode]);

  // Focus Delete Yes button when dialog opens
  useEffect(() => {
    if (deleteConfirmOpen) {
      setTimeout(() => {
        deleteYesRef.current?.focus();
      }, 100);
    }
  }, [deleteConfirmOpen]);

  // Sync overall discount amount when cart or percentage changes
  // Sync overall discount amount when cart or percentage changes
  useEffect(() => {
    const totals = calculateTotals();
    const itemDiscounts = cart.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
    const baseForDiscount = totals.subtotal + totals.taxAmount - itemDiscounts;

    if (discountType === 'PERCENTAGE' && overallDiscountPercentage > 0) {
      const newDiscountAmt = baseForDiscount * (overallDiscountPercentage / 100);
      const roundedDiscount = Math.round(newDiscountAmt * 100) / 100;
      if (Math.abs(roundedDiscount - discount) > 0.001) {
        setDiscount(roundedDiscount);
      }
    } else if (discountType === 'AMOUNT') {
      const recalcPercent = baseForDiscount > 0 ? (discount / baseForDiscount) * 100 : 0;
      const roundedPercent = Math.round(recalcPercent * 100) / 100;
      if (Math.abs(roundedPercent - overallDiscountPercentage) > 0.001) {
        setOverallDiscountPercentage(roundedPercent);
      }
    }
  }, [cart, overallDiscountPercentage, calculateTotals, discountType, discount]);

  // Load all products initially
  const loadAllProducts = useCallback(async () => {
    try {
      // Check if user has a location assigned
      let user = JSON.parse(localStorage.getItem('user')) || {};
      let userLocationData = user?.location || null;
      let hasLocation = userLocationData && userLocationData.id;

      // If location is missing, try to fetch current user data from backend
      if (!hasLocation) {
        console.log('[POSKeyboard] Location not found in localStorage, fetching current user from backend...');
        try {
          const axios = (await import('../../utils/axios')).default;
          const response = await axios.get('/auth/me');
          if (response.data && response.data.location) {
            user = response.data;
            userLocationData = response.data.location;
            hasLocation = userLocationData && userLocationData.id;
            // Update localStorage with full user object
            localStorage.setItem('user', JSON.stringify(user));
            console.log('[POSKeyboard] ✅ Fetched user location from backend:', userLocationData);
          }
        } catch (fetchError) {
          console.warn('[POSKeyboard] Could not fetch user location from backend:', fetchError);
        }
      }

      if (hasLocation) {
        console.log('[POSKeyboard] ✅ Location user detected:', userLocationData.name, '(ID:', userLocationData.id + ')');
      } else {
        console.log('[POSKeyboard] ℹ️ Warehouse/Admin user - no location assigned');
      }

      let activeProducts = [];

      if (hasLocation) {
        // Location user: Fetch products from location_products table
        console.log('[POSKeyboard] Location user detected - fetching location products for location:', userLocationData.name);
        const locationProducts = await productService.getLocationProducts();

        console.log('[POSKeyboard] Total location products received:', (locationProducts || []).length);

        // Convert location products to product format and filter by active status
        activeProducts = (locationProducts || [])
          .map(lp => ({
            id: lp.productId,
            name: lp.productName,
            sku: lp.sku || lp.productCode, // Use actual SKU if available, fallback to productCode
            barcode: lp.barcode || lp.productBarcode || lp.productCode,
            code: lp.productCode,
            quantity: lp.quantity,
            sellingPrice: lp.sellingPrice || lp.purchasePrice || 0,
            salePrice: lp.sellingPrice || lp.purchasePrice || 0,
            unitPrice: lp.sellingPrice || lp.purchasePrice || 0,
            wholesalePrice: lp.wholesalePrice != null ? lp.wholesalePrice : (lp.wholesale_price != null ? lp.wholesale_price : null),
            purchasePrice: lp.purchasePrice || 0,
            minSalePrice: lp.minSalePrice || lp.sellingPrice || 0,
            imageUrl: lp.imageUrl,
            status: lp.status || 'ACTIVE',
            locationProductId: lp.id,
            // Repacked products: use availableForSale (parent qty / ratio); normal: use quantity
            locationQuantity: lp.availableForSale != null ? Number(lp.availableForSale) : (lp.quantity != null ? Number(lp.quantity) : 0),
            locationId: lp.locationId,
            locationName: lp.locationName,
            unit: lp.unit || lp.productUnit || null
          }))
          .filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');

        console.log('[POSKeyboard] Loaded', activeProducts.length, 'active location products');
      } else {
        // Warehouse/Admin user: Fetch from products table
        console.log('[POSKeyboard] Warehouse/Admin user - fetching all products');
        const res = await productService.getAllProducts(0, 1000, 'name', 'asc');
        const list = Array.isArray(res) ? res : (res?.content || []);
        activeProducts = list.filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');
        console.log('[POSKeyboard] Loaded', activeProducts.length, 'warehouse products');
      }

      setAllProducts(activeProducts);
      console.log('Loaded products for autocomplete:', activeProducts.length);
      console.log('Sample products:', activeProducts.slice(0, 3).map(p => ({ name: p.name, sku: p.sku, status: p.status })));
    } catch (e) {
      console.error('Failed to load products', e);
      // Desktop offline: try local SQLite first (faster when backend is down)
      if (window.electronAPI?.getOfflineProducts) {
        try {
          const offlineList = await window.electronAPI.getOfflineProducts();
          const mapped = (offlineList || []).map(p => ({
            id: p.id,
            name: p.name,
            sku: p.sku,
            barcode: p.barcode,
            sellingPrice: p.selling_price ?? 0,
            salePrice: p.selling_price ?? 0,
            unitPrice: p.selling_price ?? 0,
            quantity: p.quantity ?? 0,
            unit: p.unit_name ? { id: p.unit_id, name: p.unit_name } : null,
            status: p.status || 'ACTIVE',
            parentProductId: p.parent_product_id ?? p.parentProductId ?? null
          }));
          setAllProducts(mapped);
          console.log('[POSKeyboard] Loaded products from offline DB:', mapped.length);
          return;
        } catch (e3) {
          console.error('Offline products fallback failed', e3);
        }
      }
      // Fallback: try search (when online but main endpoint failed)
      try {
        const fallbackRes = await productService.searchProducts('a');
        const fallbackList = Array.isArray(fallbackRes) ? fallbackRes : (fallbackRes?.content || []);
        const fallbackActive = fallbackList.filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');
        setAllProducts(fallbackActive);
        console.log('Loaded products (fallback):', fallbackActive.length);
      } catch (e2) {
        console.error('Fallback also failed', e2);
      }
    }
  }, []);

  // Load units
  const loadUnits = useCallback(async () => {
    try {
      const user = getCurrentUser();
      const companyId = user?.companyId || 1;
      const response = await unitService.getUnitsByCompany(companyId);
      const unitsList = Array.isArray(response) ? response : (response?.content || []);
      setUnits(unitsList);
      console.log('Loaded units:', unitsList.length);
    } catch (error) {
      console.error('Failed to load units:', error);
      // Set default units if API fails
      setUnits([
        { id: 1, name: 'Piece', abbreviation: 'PCS' },
        { id: 2, name: 'Box', abbreviation: 'BOX' },
        { id: 3, name: 'Kilogram', abbreviation: 'KG' },
        { id: 4, name: 'Gram', abbreviation: 'GM' },
        { id: 5, name: 'Liter', abbreviation: 'L' },
        { id: 6, name: 'Meter', abbreviation: 'M' }
      ]);
    }
  }, []);

  // Load customers
  const loadCustomers = useCallback(async () => {
    try {
      const res = await customerService.getAllCustomers({ page: 0, size: 1000, sort: 'name', direction: 'ASC' });
      const list = Array.isArray(res) ? res : (res?.content || []);
      setCustomers(list);

      // Always set "Walk-in Customer" as default if not already set
      // This ensures it's set on initial load and after refresh
      if (list.length > 0) {
        // Find Walk-in Customer by email, name, or customer ID
        const walkInCustomer = list.find(c =>
          (c.email && c.email.toLowerCase().includes('walkin')) ||
          (c.name && c.name.toLowerCase().includes('walk-in customer')) ||
          (c.customerId === '0000000000' || c.customerId === 0)
        );

        if (walkInCustomer) {
          // Only set if customer is not already set or if it's null
          setCustomer(prevCustomer => {
            if (!prevCustomer) {
              console.log('Default customer set to:', walkInCustomer.name);
              return walkInCustomer;
            }
            return prevCustomer;
          });
        }
      }
    } catch (e) {
      console.error('Failed to load customers', e);
      // Desktop offline: load from local SQLite
      if (window.electronAPI?.getOfflineCustomers) {
        try {
          const offlineList = await window.electronAPI.getOfflineCustomers();
          const mapped = (offlineList || []).map(c => ({
            id: c.id,
            name: c.name,
            phone: c.phone,
            email: c.email,
            loyaltyPoints: c.loyalty_points ?? 0
          }));
          setCustomers(mapped);
          const walkIn = mapped.find(c =>
            (c.email && c.email.toLowerCase().includes('walkin')) ||
            (c.name && c.name.toLowerCase().includes('walk-in customer'))
          );
          if (walkIn) setCustomer(prev => prev || walkIn);
          console.log('[POSKeyboard] Loaded customers from offline DB:', mapped.length);
        } catch (e2) {
          console.error('Offline customers fallback failed', e2);
        }
      }
    }
  }, []); // Remove customer dependency to ensure it always runs

  // Handle new customer form success
  const handleCustomerFormSuccess = (newCustomer, error) => {
    if (error) {
      enqueueSnackbar(error, { variant: 'error' });
    } else {
      // Add the new customer to the list
      setCustomers(prev => [...prev, newCustomer]);

      // Close the dialog
      setAddCustomerDialogOpen(false);

      // Select the new customer
      setCustomer(newCustomer);
      setCustomerInputValue(''); // Clear input value
      setNewCustomerName(''); // Clear pre-filled name

      // Show success message
      enqueueSnackbar(`Customer "${newCustomer.name}" added successfully`, { variant: 'success' });
    }
  };

  // Load locations
  const loadLocations = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/locations');
      const list = Array.isArray(res.data) ? res.data : (res.data?.content || []);
      setLocations(list);
      // Set first location as default if available
      if (list.length > 0 && !selectedLocation) {
        setSelectedLocation(list[0]);
      }
    } catch (e) {
      console.error('Failed to load locations', e);
    }
  }, [selectedLocation]);

  // Refresh customer data for latest loyalty points and balance when selected
  useEffect(() => {
    if (customer?.id && customer.id !== 1) {
      const fetchLatest = async () => {
        try {
          const fresh = await customerService.getCustomerById(customer.id);
          // Only update if it's still the same customer and data is different or points not yet in object
          setCustomer(prev => {
            if (prev?.id === fresh.id) {
              return { ...prev, ...fresh };
            }
            return prev;
          });
        } catch (e) {
          console.error('Failed to fetch fresh customer data', e);
        }
      };
      fetchLatest();
    }
  }, [customer?.id]);

  // Load salesmen for current location (or all) for POS salesman dropdown
  useEffect(() => {
    const loadSalesmen = async () => {
      try {
        const user = getCurrentUser();
        const locationId = user?.location?.id || null;
        const list = await salesmanService.list(locationId != null ? { locationId } : {});
        setSalesmen(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error('Failed to load salesmen', e);
        if (window.electronAPI?.getOfflineSalesmen) {
          try {
            const offlineList = await window.electronAPI.getOfflineSalesmen();
            setSalesmen(offlineList || []);
            console.log('[POSKeyboard] Loaded salesmen from offline DB:', (offlineList || []).length);
          } catch (e2) {
            setSalesmen([]);
          }
        } else {
          setSalesmen([]);
        }
      }
    };
    loadSalesmen();
  }, []);

  // When payment dialog opens: load loyalty settings and refresh customer for latest points
  useEffect(() => {
    if (!paymentDialogOpen) {
      setRedeemedPoints(0);
      return;
    }
    const load = async () => {
      try {
        const settings = await loyaltySettingsService.get();
        setLoyaltySettings(settings && (settings.minPurchaseAmount != null || settings.amountPerPoint != null) ? settings : null);
      } catch (e) {
        setLoyaltySettings(null);
      }
      if (customer?.id) {
        try {
          const fresh = await customerService.getCustomerById(customer.id);
          setCustomer(fresh);
        } catch (e) { /* keep current customer */ }
      }
    };
    load();
  }, [paymentDialogOpen]);

  // Load print settings (including WhatsApp share) for current location
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // Get current user to determine location
        const userStr = localStorage.getItem('user');
        let locationId = null;
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            locationId = user.location?.id;
          } catch (e) {
            console.error('Error parsing user from localStorage', e);
          }
        }

        const settings = await printSettingsService.getPrintSettings(locationId);
        if (settings) {
          if (typeof settings.enableWhatsappShare === 'boolean') {
            setEnableWhatsappShare(settings.enableWhatsappShare);
          }
          if (typeof settings.directPrint === 'boolean') {
            setDirectPrint(settings.directPrint);
          }
          if (typeof settings.previewPrint === 'boolean') {
            setPreviewPrint(settings.previewPrint);
          }
        }
      } catch (error) {
        console.error('Error loading print settings:', error);
        // Don't show error toast, just use default value
      }
    };

    loadSettings();
  }, []);

  // Effective sale rate for current price mode (retailer or wholesale). No wholesale = 0.
  const getSaleRateForMode = useCallback((product) => {
    if (!product) return 0;
    if (priceMode === 'wholesale') {
      const w = product.wholesalePrice ?? product.wholesale_price;
      return (w != null && Number(w) >= 0) ? Number(w) : 0;
    }
    return Number(product.sellingPrice ?? product.salePrice ?? product.unitPrice ?? 0) || 0;
  }, [priceMode]);

  // Get available units for a product (Main Unit, Sub Unit 1, Sub Unit 2). Prices respect priceMode.
  const getAvailableUnits = useCallback((product) => {
    if (!product) return [];

    const mainPrice = getSaleRateForMode(product);
    const availableUnits = [];

    // Main Unit (always available)
    if (product.unit) {
      availableUnits.push({
        id: product.unit.id,
        name: product.unit.name,
        abbreviation: product.unit.abbreviation,
        price: mainPrice,
        type: 'MAIN'
      });
    } else if (product.unitId && units.length > 0) {
      const mainUnit = units.find(u => u.id === product.unitId);
      if (mainUnit) {
        availableUnits.push({
          id: mainUnit.id,
          name: mainUnit.name,
          abbreviation: mainUnit.abbreviation,
          price: mainPrice,
          type: 'MAIN'
        });
      }
    }

    // Sub Unit 1: in wholesale mode we don't have wholesale sub-unit price, use 0
    if (product.subUnit1 && (priceMode === 'retailer' ? product.subUnit1SellingPrice : true)) {
      const sub1Price = priceMode === 'wholesale' ? 0 : (product.subUnit1SellingPrice || 0);
      availableUnits.push({
        id: product.subUnit1.id,
        name: product.subUnit1.name,
        abbreviation: product.subUnit1.abbreviation,
        price: sub1Price,
        type: 'SUB_UNIT1'
      });
    }

    // Sub Unit 2: in wholesale mode we don't have wholesale sub-unit price, use 0
    if (product.subUnit2 && (priceMode === 'retailer' ? product.subUnit2SellingPrice : true)) {
      const sub2Price = priceMode === 'wholesale' ? 0 : (product.subUnit2SellingPrice || 0);
      availableUnits.push({
        id: product.subUnit2.id,
        name: product.subUnit2.name,
        abbreviation: product.subUnit2.abbreviation,
        price: sub2Price,
        type: 'SUB_UNIT2'
      });
    }

    return availableUnits;
  }, [units, priceMode, getSaleRateForMode]);

  // Search product by code/SKU/barcode
  const searchProduct = useCallback(async (code, rowIndex) => {
    if (!code || code.trim() === '') return;

    try {
      setSearchingProduct(true);

      // Check if user has a location - if yes, search in allProducts (which are location products)
      const user = JSON.parse(localStorage.getItem('user')) || {};
      const userLocationData = user?.location || null;
      const hasLocation = userLocationData && userLocationData.id;

      let product = null;

      if (hasLocation && allProducts.length > 0) {
        // Location user: Search in already loaded location products (SKU, productCode, barcode, code)
        const query = code.toLowerCase().trim();
        product = allProducts.find(p =>
          (p.sku && p.sku.toLowerCase() === query) ||
          (p.productCode && p.productCode.toLowerCase() === query) ||
          (p.product_code && p.product_code.toLowerCase() === query) ||
          (p.barcode && p.barcode.toLowerCase() === query) ||
          (p.code && p.code.toLowerCase() === query)
        );

        // If not found in list, try barcode lookup then SKU lookup (e.g. scanned barcode or manual SKU entry not in preloaded list)
        if (!product) {
          try {
            const byBarcode = await productService.getProductByBarcode(code.trim());
            if (byBarcode && (!byBarcode.status || byBarcode.status.toUpperCase() === 'ACTIVE')) {
              product = byBarcode;
            }
          } catch (e) {
            // ignore, try SKU next
          }
        }
        if (!product) {
          try {
            const bySku = await productService.getProductBySku(code.trim());
            if (bySku && (!bySku.status || bySku.status.toUpperCase() === 'ACTIVE')) {
              product = bySku;
            }
          } catch (e) {
            // ignore, try searchProducts next
          }
        }
        // Fallback: use backend search (handles partial matches like "e", "phi", "Philips")
        if (!product) {
          try {
            const res = await productService.searchProducts(code.trim());
            const list = (Array.isArray(res) ? res : (res?.content || []))
              .filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');
            if (list.length > 0) {
              product = list[0];
            }
          } catch (e) {
            // ignore, keep product null
          }
        }

        // If product found but missing sub-unit data, fetch full product details
        if (product && product.id && (!product.subUnit1 && !product.subUnit2)) {
          try {
            const fullProduct = await productService.getProductById(product.id);
            // Merge full product data with location-specific data
            product = {
              ...fullProduct,
              quantity: product.quantity,
              locationProductId: product.locationProductId,
              locationQuantity: product.locationQuantity,
              locationId: product.locationId,
              locationName: product.locationName
            };
          } catch (e) {
            console.warn('Failed to fetch full product details, using location product data:', e);
          }
        }
      } else {
        // Warehouse/Admin user: Search via API (name, SKU, barcode) and filter by active status
        try {
          const res = await productService.searchProducts(code);
          const list = (Array.isArray(res) ? res : (res?.content || []))
            .filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE');

          if (list.length > 0) {
            product = list[0];
          }
        } catch (searchErr) {
          // searchProducts may return 400 for short keyword (e.g. some barcodes); try barcode lookup
        }

        // Fallback: direct barcode lookup then SKU lookup (for scanned barcode or manual SKU/product code entry)
        if (!product) {
          try {
            const byBarcode = await productService.getProductByBarcode(code.trim());
            if (byBarcode && (!byBarcode.status || byBarcode.status.toUpperCase() === 'ACTIVE')) {
              product = byBarcode;
            }
          } catch (e) {
            // ignore, try SKU next
          }
        }
        if (!product) {
          try {
            const bySku = await productService.getProductBySku(code.trim());
            if (bySku && (!bySku.status || bySku.status.toUpperCase() === 'ACTIVE')) {
              product = bySku;
            }
          } catch (e) {
            // ignore
          }
        }

        // If product found but missing sub-unit data, fetch full product details
        if (product && product.id && (!product.subUnit1 && !product.subUnit2)) {
          try {
            product = await productService.getProductById(product.id);
          } catch (e) {
            console.warn('Failed to fetch full product details, using search result:', e);
          }
        }
      }

      if (product) {
        // Get available units for this product
        const availableUnits = getAvailableUnits(product);

        // Default to Main Unit (first in availableUnits array)
        const defaultUnitData = availableUnits.length > 0 ? availableUnits[0] : null;
        const defaultPrice = defaultUnitData ? defaultUnitData.price : getSaleRateForMode(product);

        // Get default unit object
        let defaultUnit = null;
        if (defaultUnitData) {
          defaultUnit = {
            id: defaultUnitData.id,
            name: defaultUnitData.name,
            abbreviation: defaultUnitData.abbreviation
          };
        } else if (product.unit) {
          defaultUnit = product.unit;
        } else if (product.unitId && units.length > 0) {
          defaultUnit = units.find(u => u.id === product.unitId) || units[0];
        } else if (units.length > 0) {
          defaultUnit = units[0];
        }

        const newCart = [...cart];
        newCart[rowIndex] = {
          ...newCart[rowIndex],
          itemCode: product.sku || product.productCode || product.code || code,
          productName: product.name,
          price: defaultPrice,
          product: product,
          selectedUnit: defaultUnit,
        };
        setCart(newCart);

        // Move focus to next field in tab order (e.g. Discount if configured), or add new row
        setTimeout(() => {
          const fn = handleTabOrderEnterRef.current;
          if (typeof fn === 'function') {
            fn(rowIndex, 'itemCode');
          } else if (qtyRefs.current[rowIndex]) {
            qtyRefs.current[rowIndex].focus();
            qtyRefs.current[rowIndex].select();
          }
        }, 100);
      } else {
        enqueueSnackbar('Product not found', { variant: 'warning' });
      }
    } catch (e) {
      console.error('Product search failed', e);
      enqueueSnackbar('Failed to search product', { variant: 'error' });
    } finally {
      setSearchingProduct(false);
    }
  }, [cart, enqueueSnackbar, allProducts, units, getAvailableUnits, getSaleRateForMode]);

  // Handle item code input change (for autocomplete)
  const handleItemCodeInputChange = async (rowIndex, value) => {
    const newCart = [...cart];
    newCart[rowIndex].itemCode = value;
    setCart(newCart);

    // If nothing typed, clear options
    if (!value || value.trim().length === 0) {
      const newOptions = { ...productOptions };
      newOptions[rowIndex] = [];
      setProductOptions(newOptions);
      return;
    }

    const query = value.toLowerCase();

    try {
      let filtered = [];

      if (allProducts && allProducts.length > 0) {
        // Use already loaded products for fast local filtering
        filtered = allProducts.filter(p =>
          (p.name && p.name.toLowerCase().includes(query)) ||
          (p.sku && p.sku.toLowerCase().includes(query)) ||
          (p.productCode && p.productCode.toLowerCase().includes(query)) ||
          (p.product_code && p.product_code.toLowerCase().includes(query)) ||
          (p.barcode && p.barcode.toLowerCase().includes(query)) ||
          (p.code && p.code.toLowerCase().includes(query))
        ).slice(0, 10);
      } else {
        // Fallback: call backend search if preload failed or returned empty
        console.log('[POSKeyboard] allProducts empty, performing server search for:', value);
        const response = await productService.searchProducts(value);
        const list = Array.isArray(response) ? response : (response?.content || []);
        filtered = list
          .filter(p => !p.status || p.status.toUpperCase() === 'ACTIVE')
          .slice(0, 10);
      }

      const newOptions = { ...productOptions };
      newOptions[rowIndex] = filtered;
      setProductOptions(newOptions);
      console.log('[POSKeyboard] Autocomplete options for "', value, '":', filtered.length);
    } catch (error) {
      console.error('[POSKeyboard] Autocomplete search failed for value:', value, error);
    }
  };

  // Handle product selection from autocomplete
  const handleProductSelect = async (rowIndex, product) => {
    if (!product) return;

    // If product missing sub-unit data, fetch full product details
    let fullProduct = product;
    if (product.id && (!product.subUnit1 && !product.subUnit2)) {
      try {
        fullProduct = await productService.getProductById(product.id);
        // Preserve location-specific data if it exists
        if (product.locationProductId) {
          fullProduct = {
            ...fullProduct,
            quantity: product.quantity,
            locationProductId: product.locationProductId,
            locationQuantity: product.locationQuantity,
            locationId: product.locationId,
            locationName: product.locationName
          };
        }
      } catch (e) {
        console.warn('Failed to fetch full product details, using autocomplete result:', e);
        fullProduct = product;
      }
    }

    // Get available units for this product
    const availableUnits = getAvailableUnits(fullProduct);

    // Default to Main Unit (first in availableUnits array)
    const defaultUnitData = availableUnits.length > 0 ? availableUnits[0] : null;
    const price = defaultUnitData ? defaultUnitData.price : getSaleRateForMode(fullProduct);
    const productId = fullProduct.id;

    // Get default unit object
    let defaultUnit = null;
    if (defaultUnitData) {
      defaultUnit = {
        id: defaultUnitData.id,
        name: defaultUnitData.name,
        abbreviation: defaultUnitData.abbreviation
      };
    } else if (fullProduct.unit) {
      defaultUnit = fullProduct.unit;
    } else if (fullProduct.unitId && units.length > 0) {
      defaultUnit = units.find(u => u.id === fullProduct.unitId) || units[0];
    } else if (units.length > 0) {
      defaultUnit = units[0];
    }

    // Check if this product already exists in the cart (any row)
    const existingProductIndex = cart.findIndex(
      (item) => item.product && item.product.id === productId
    );

    const newCart = [...cart];

    if (existingProductIndex !== -1) {
      // Product already exists in some row - check stock before increasing
      const currentItem = newCart[existingProductIndex];
      const available = getAvailableStock(currentItem);

      if (available !== undefined && available !== null && (currentItem.quantity + 1) > available) {
        enqueueSnackbar(`Low Stock: Only ${available} available`, { variant: 'warning' });
        // If scan happened in a different row, clear that scanning row
        if (rowIndex !== existingProductIndex) {
          newCart[rowIndex] = {
            ...newCart[rowIndex],
            itemCode: '',
            productName: '',
            price: 0,
            discount: 0,
            product: null,
            selectedUnit: null,
          };
        }
        setTimeout(() => {
          if (itemCodeRefs.current[rowIndex]) itemCodeRefs.current[rowIndex].focus();
        }, 100);
      } else {
        newCart[existingProductIndex].quantity += 1;

        // Move existing product row to last so user sees the updated qty without scrolling (e.g. soap was row 2, now row 12)
        const itemToMove = newCart[existingProductIndex];
        newCart.splice(existingProductIndex, 1);
        newCart.push(itemToMove);

        // If scan happened in a *different* row, remove that row so there's no empty row in the middle, then add one empty row at end
        if (rowIndex !== existingProductIndex) {
          const removeIndex = rowIndex < existingProductIndex ? rowIndex : rowIndex - 1;
          newCart.splice(removeIndex, 1);
          const newId = newCart.length > 0 ? Math.max(...newCart.map(c => c.id)) + 1 : 1;
          newCart.push({
            id: newId,
            itemCode: '',
            productName: '',
            quantity: 1,
            price: 0,
            discount: 0,
            discountPercentage: 0,
            discountAmount: 0,
            product: null,
            selectedUnit: null,
          });
        }

        // Select last row and focus its item code (not qty), so qty only increases and pressing Enter does not add a new row
        const lastIndex = newCart.length - 1;
        setSelectedRowIndex(lastIndex);
        setTimeout(() => {
          if (itemCodeRefs.current[lastIndex]) {
            itemCodeRefs.current[lastIndex].focus();
          }
        }, 100);
      }
    } else {
      // New product - add it to the current row
      let finalPrice = price;
      if (nonGstMode && fullProduct?.tax?.rate != null) {
        const taxRate = fullProduct.tax.rate / 100;
        finalPrice = price * (1 + taxRate);
        finalPrice = Math.round(finalPrice * 100) / 100;
      }
      const newItem = {
        ...newCart[rowIndex],
        itemCode: fullProduct.sku || fullProduct.name,
        productName: fullProduct.name,
        price: finalPrice,
        discount: 0,
        product: fullProduct,
        selectedUnit: defaultUnit,
        quantity: 1 // Default to 1
      };

      // Check stock for new item
      const available = getAvailableStock(newItem);
      if (available !== undefined && available !== null && 1 > available) {
        enqueueSnackbar(`Out of Stock: Product has ${available} available`, { variant: 'warning' });
        // We still add it but maybe the user should know. 
        // If strictly 0 available, maybe we shouldn't add? 
        // For now, allowing add but showing warning is safer given the prompt "show this message".
        // But "unexpected error" suggests the backend fails. 
        // If I don't add it, I avoid the error. 
        // If 0 available, I will add it because maybe they have physical stock not in system?
        // But the backend will fail.
        // Let's add it (so they see what they scanned) but the warning helps.
      }

      newCart[rowIndex] = newItem;

      // Move focus to next field in tab order (e.g. Discount if configured), or add new row
      setTimeout(() => {
        const fn = handleTabOrderEnterRef.current;
        if (typeof fn === 'function') {
          fn(rowIndex, 'itemCode');
        } else if (qtyRefs.current[rowIndex]) {
          qtyRefs.current[rowIndex].focus();
          qtyRefs.current[rowIndex].select();
        }
      }, 100);
    }

    setCart(newCart);
  };

  // Handle item code blur (search product)
  const handleItemCodeBlur = (rowIndex, value) => {
    if (value && !cart[rowIndex].product) {
      searchProduct(value, rowIndex);
    }
  };

  // Handle quantity change
  const handleQuantityChange = (rowIndex, value) => {
    const newCart = [...cart];
    const newQty = Math.max(1, Number(value) || 1);

    // Check stock availability
    if (newCart[rowIndex].product) { // Only check if product is set
      const available = getAvailableStock(newCart[rowIndex]);
      // If stock is tracked (available > 0 or explicit 0), and new quantity exceeds available
      if (available !== undefined && available !== null && newQty > available) {
        enqueueSnackbar(`Low Stock: Only ${available} available`, { variant: 'warning' });
        // Cap the quantity to available stock
        newCart[rowIndex].quantity = available > 0 ? available : 1;
      } else {
        newCart[rowIndex].quantity = newQty;
      }
    } else {
      newCart[rowIndex].quantity = newQty;
    }
    setCart(newCart);
  };

  // Handle price change
  const handlePriceChange = (rowIndex, value) => {
    const newCart = [...cart];
    newCart[rowIndex].price = Number(value) || 0;
    setCart(newCart);
  };

  // Handle overall discount change with bidirectional conversion
  const handleOverallDiscountChange = (value, type) => {
    const totals = calculateTotals();
    const itemDiscounts = cart.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
    const baseForOverallDiscount = nonGstMode ? totals.subtotal : (totals.subtotal + totals.taxAmount - itemDiscounts);

    setDiscountType(type === 'percentage' ? 'PERCENTAGE' : 'AMOUNT');

    let discountPercent = 0;
    let discountAmt = 0;

    if (value === '' || value === null || value === undefined) {
      setOverallDiscountPercentage(0);
      setDiscount(0);
    } else {
      const numValue = parseFloat(value) || 0;

      if (type === 'percentage') {
        discountPercent = Math.min(numValue, 100);
        discountAmt = baseForOverallDiscount * (discountPercent / 100);

        setOverallDiscountPercentage(numValue); // Store exact value typed
        setDiscount(Math.round(discountAmt * 100) / 100);
      } else {
        discountAmt = Math.min(numValue, baseForOverallDiscount);
        discountPercent = baseForOverallDiscount > 0 ? (discountAmt / baseForOverallDiscount) * 100 : 0;

        setDiscount(numValue); // Store exact value typed
        setOverallDiscountPercentage(Math.round(discountPercent * 100) / 100);
      }
    }
  };

  // Handle discount change with bidirectional conversion
  const handleItemDiscountChange = (rowIndex, value, type) => {
    const newCart = [...cart];
    const item = newCart[rowIndex];
    const baseAmount = (item.quantity || 0) * (item.price || 0);

    let discountPercent = 0;
    let discountAmt = 0;

    // If field is empty, set to 0
    if (value === '' || value === null || value === undefined) {
      discountPercent = 0;
      discountAmt = 0;
    } else {
      const numValue = parseFloat(value) || 0;

      if (type === 'percentage') {
        // User entered percentage - calculate amount
        discountPercent = Math.min(numValue, 100); // Cap at 100%
        discountAmt = baseAmount * (discountPercent / 100);
      } else {
        // User entered amount - calculate percentage
        discountAmt = Math.min(numValue, baseAmount); // Cap at base amount
        discountPercent = baseAmount > 0 ? (discountAmt / baseAmount) * 100 : 0;
      }
    }

    newCart[rowIndex].discountPercentage = Math.round(discountPercent * 100) / 100;
    newCart[rowIndex].discountAmount = Math.round(discountAmt * 100) / 100;
    newCart[rowIndex].discount = discountAmt; // Keep for backward compatibility

    setCart(newCart);
  };



  // Helper: get available stock for a cart row in the same UOM as the row's selected unit.
  // Stock is always stored in main unit; we convert to selected unit so quantity vs available comparison is correct.
  // Repacked products: have no separate stock (derived from parent); when offline we allow - backend validates on sync.
  const getAvailableStock = (item) => {
    if (!item || !item.product) return 0;
    const p = item.product;
    // Repacked products: stock comes from parent; when offline we cannot compute it - allow sale (backend validates)
    if (p.parentProductId != null) return 999999;
    // Available stock is always in main unit (e.g. 50 kg)
    const availableMain = typeof p.locationQuantity === 'number'
      ? p.locationQuantity
      : (typeof p.quantity === 'number' ? p.quantity : Number(p.quantity));
    if (availableMain == null || isNaN(availableMain)) return 0;

    const selectedUnitId = item.selectedUnit?.id != null ? Number(item.selectedUnit.id) : null;
    const mainUnitId = p.unit?.id != null ? Number(p.unit.id) : (p.unitId != null ? Number(p.unitId) : null);
    const sub1Id = p.subUnit1?.id != null ? Number(p.subUnit1.id) : null;
    const sub2Id = p.subUnit2?.id != null ? Number(p.subUnit2.id) : null;

    // No unit selected or main unit selected: return as-is (main unit)
    if (selectedUnitId == null || selectedUnitId === mainUnitId) {
      return availableMain;
    }

    // Sub Unit 1: 1 main = subUnit1ConversionRatio sub1 (e.g. 1 kg = 1000 g)
    const ratio1 = p.subUnit1ConversionRatio != null ? Number(p.subUnit1ConversionRatio) : 0;
    if (sub1Id != null && selectedUnitId === sub1Id && ratio1 > 0) {
      return availableMain * ratio1;
    }

    // Sub Unit 2: 1 main = ratio1 * ratio2 sub2
    const ratio2 = p.subUnit2ConversionRatio != null ? Number(p.subUnit2ConversionRatio) : 0;
    if (sub2Id != null && selectedUnitId === sub2Id && ratio1 > 0 && ratio2 > 0) {
      return availableMain * ratio1 * ratio2;
    }

    return availableMain;
  };

  // Clear selected product for a row and reset inputs
  const clearRowProduct = (rowIndex) => {
    const newCart = [...cart];
    if (!newCart[rowIndex]) return;
    newCart[rowIndex] = {
      ...newCart[rowIndex],
      itemCode: '',
      productName: '',
      price: 0,
      product: null,
      selectedUnit: null
    };
    setCart(newCart);
    const newOptions = { ...productOptions };
    newOptions[rowIndex] = [];
    setProductOptions(newOptions);
    setTimeout(() => {
      itemCodeRefs.current[rowIndex]?.focus();
    }, 100);
  };

  // Handle UOM change
  const handleUOMChange = (rowIndex, unitId) => {
    const newCart = [...cart];
    const item = newCart[rowIndex];

    if (!item.product) return;

    // Get available units for this product
    const availableUnits = getAvailableUnits(item.product);
    const selectedUnitData = availableUnits.find(u => u.id === parseInt(unitId));

    if (selectedUnitData) {
      // Update selected unit and price
      newCart[rowIndex].selectedUnit = {
        id: selectedUnitData.id,
        name: selectedUnitData.name,
        abbreviation: selectedUnitData.abbreviation
      };
      newCart[rowIndex].price = selectedUnitData.price ?? getSaleRateForMode(item.product);
      setCart(newCart);
    } else {
      // Fallback: if unit not found in available units, use main unit price for current mode
      const selectedUnit = units.find(u => u.id === parseInt(unitId));
      if (selectedUnit) {
        newCart[rowIndex].selectedUnit = selectedUnit;
        newCart[rowIndex].price = getSaleRateForMode(item.product);
        setCart(newCart);
      }
    }
  };

  // Add new row
  const addNewRow = useCallback(() => {
    const newId = cart.length > 0 ? Math.max(...cart.map(c => c.id)) + 1 : 1;
    setCart([...cart, {
      id: newId,
      itemCode: '',
      productName: '',
      quantity: 1,
      price: 0,
      discount: 0,
      discountPercentage: 0,
      discountAmount: 0,
      product: null,
      selectedUnit: null
    }]);

    // Focus on new row's item code
    setTimeout(() => {
      const newIndex = cart.length;
      if (itemCodeRefs.current[newIndex]) {
        itemCodeRefs.current[newIndex].focus();
      }
    }, 100);
  }, [cart]);

  const handleTabOrderEnter = useCallback((rowIndex, currentKey) => {
    const idx = orderedFieldKeys.indexOf(currentKey);
    if (idx < 0) return;
    if (idx + 1 < orderedFieldKeys.length) {
      const nextKey = orderedFieldKeys[idx + 1];
      setTimeout(() => focusRef(nextKey, rowIndex), 50);
    } else {
      addNewRow();
    }
  }, [orderedFieldKeys, focusRef, addNewRow]);

  handleTabOrderEnterRef.current = handleTabOrderEnter;

  // Delete row
  const deleteRow = useCallback((rowIndex) => {
    if (cart.length === 1) {
      // Reset the only row
      setCart([{ id: 1, itemCode: '', productName: '', quantity: 1, price: 0, discount: 0, discountPercentage: 0, discountAmount: 0, product: null, selectedUnit: null }]);
    } else {
      setCart(prevCart => prevCart.filter((_, idx) => idx !== rowIndex));
    }
  }, [cart]);

  // Clear all
  const clearAll = useCallback(() => {
    setCart([{ id: 1, itemCode: '', productName: '', quantity: 1, price: 0, discount: 0, discountPercentage: 0, discountAmount: 0, product: null, selectedUnit: null }]);
    setDiscount(0);
    setOverallDiscountPercentage(0);
    setNotes('');
    setReceivedAmount('');

    // Reset customer to Walk-in Customer
    const walkInCustomer = customers.find(c =>
      (c.email && c.email.toLowerCase().includes('walkin')) ||
      (c.name && c.name.toLowerCase().includes('walk-in customer')) ||
      (c.customerId === '0000000000' || c.customerId === 0)
    );
    if (walkInCustomer) {
      setCustomer(walkInCustomer);
    } else {
      setCustomer(null);
    }

    // Reload recent orders to show the latest transaction
    loadRecentOrders();

    setTimeout(() => {
      if (itemCodeRefs.current[0]) {
        itemCodeRefs.current[0].focus();
      }
    }, 100);
  }, [customers, setCustomer, loadRecentOrders]);

  // Handle hold bill success
  const handleHoldBillSuccess = useCallback(() => {
    enqueueSnackbar('Bill held successfully', { variant: 'success' });
    clearAll();
  }, [clearAll, enqueueSnackbar]);

  // Handle processing a hold bill
  const handleProcessHoldBill = async (bill) => {
    try {
      // If there is already an unbilled cart with real items, confirm before replacing it
      const hasExistingItems = cart.some(ci => ci.product && Number(ci.quantity) > 0);
      if (hasExistingItems) {
        const proceed = window.confirm(
          'Current bill is not yet billed. Do you want to discard it and load the selected hold bill?'
        );
        if (!proceed) {
          return; // Keep current cart as-is
        }
      }

      // Load items into cart
      const newCart = bill.items.map((item, index) => ({
        id: index + 1,
        itemCode: item.itemCode || '',
        productName: item.productName,
        quantity: item.quantity,
        price: item.unitPrice,
        product: {
          id: item.productId,
          name: item.productName,
          sku: item.itemCode || null,
          barcode: item.itemCode || null,
          sellingPrice: item.unitPrice,
          salePrice: item.unitPrice,
          tax: item.taxRate != null ? { rate: Number(item.taxRate) } : null
        },
        selectedUnit: null
      }));

      setCart(newCart);

      // Set customer
      if (bill.customerId) {
        // Try to find the full customer object if needed, but for now just set what we have
        setCustomer({
          id: bill.customerId,
          name: bill.customerName
        });
      }

      // Delete the hold bill since it's now in the cart
      await holdBillService.deleteHoldBill(bill.id);

      enqueueSnackbar('Hold bill loaded successfully', { variant: 'success' });
      setViewHoldBillsModalOpen(false); // Close the view hold bills modal
    } catch (error) {
      console.error('Error processing hold bill:', error);
      enqueueSnackbar('Failed to load hold bill', { variant: 'error' });
    }
  };

  // Handle payment
  // paymentMethodOverride: when provided (JSON string from Split Bill), use it instead of paymentMethod state
  const handlePayment = useCallback(async (paymentMethodOverride = null) => {
    const validItems = cart.filter(item => item.product && item.quantity > 0);

    if (validItems.length === 0) {
      enqueueSnackbar('Please add at least one item', { variant: 'warning' });
      return;
    }

    try {
      const user = JSON.parse(localStorage.getItem('user')) || {};
      const companyId = user?.company?.id || user?.companyId || 1;
      const totals = calculateTotals();

      const items = validItems.map((ci) => ({
        productId: ci.product.id,
        quantity: Number(ci.quantity),
        unitPrice: Number(ci.price),
        taxId: nonGstMode ? null : (ci.product?.tax?.id || null),
        discount: Number(ci.discount || 0),
        unitId: ci.selectedUnit?.id || null, // Send the selected unit ID
      }));

      // Use override (split bill JSON) or regular payment method
      const backendPaymentMethod = paymentMethodOverride != null ? paymentMethodOverride : paymentMethod;

      // Determine payment status: split with credit -> PENDING, else PAID
      let isCredit = backendPaymentMethod === 'CREDIT';
      let isImmediatePayment = ['CASH', 'CREDIT_CARD', 'PAYNOW'].includes(backendPaymentMethod);
      if (typeof backendPaymentMethod === 'string' && backendPaymentMethod.startsWith('{')) {
        try {
          const split = JSON.parse(backendPaymentMethod);
          isCredit = split?.payments?.some((p) => p.mode === 'CREDIT') || false;
          isImmediatePayment = !isCredit;
        } catch (e) {
          isCredit = false;
          isImmediatePayment = true;
        }
      }
      const paymentStatus = isImmediatePayment ? 'PAID' : (isCredit ? 'PENDING' : 'PAID');

      // For admin users (no location assigned), locationId should be null
      // For location users, use their assigned location
      const userLocationId = user?.location?.id || user?.locationId || null;
      // Only set locationId if user has a location (location users), otherwise null for admin
      const locationId = userLocationId || null;

      // Validate credit sale (single CREDIT or split with CREDIT)
      if (isCredit && (!customer || !customer.id)) {
        alert('Customer selection is required for credit sales');
        return;
      }

      // When round-off is enabled, send rounded total + roundOffAmount so backend stores them (DB total = rounded; bill print shows it)
      const sendRoundOff = roundOffEnabled && totals.roundOff != null;

      const request = {
        customerId: isCredit ? customer.id : (customer?.id ?? null),
        orderDate: new Date().toISOString().slice(0, 10),
        companyId,
        items,
        paymentMethod: backendPaymentMethod, // Use backend expected payment method
        paymentStatus: paymentStatus,
        status: isQuotationMode ? 'PENDING' : 'DELIVERED', // Quotations should be PENDING, not DELIVERED
        notes: notes || 'POS sale',
        subtotal: totals.subtotal,
        taxAmount: totals.taxAmount,
        total: totals.total, // When round-off enabled this is the rounded amount (e.g. 174)
        discount: discount,
        shippingCost: 0,
        locationId: locationId, // Send location ID for location-based sales
        salesmanId: selectedSalesmanId ? parseInt(selectedSalesmanId, 10) : null, // Optional: attribute to salesman
        updateCreditBalance: false, // Don't update credit balance here
        isQuotation: isQuotationMode, // Add quotation flag
        nonGst: nonGstMode, // Non-GST bill: tax-inclusive rate, no GST displayed
        redeemedPoints: redeemedPoints > 0 ? redeemedPoints : null, // Loyalty points redeemed on this bill
        ...(sendRoundOff && { roundOffAmount: totals.roundOff }), // Backend stores total + round_off_amount; bill print uses rounded total
      };

      console.log('[POSKeyboard] Creating/updating sales order with data:', {
        paymentMethod: backendPaymentMethod,
        paymentStatus,
        total: totals.total,
        locationId,
        itemsCount: items.length,
        customerId: request.customerId
      });

      // Decide whether to create, convert quotation, or update existing order
      let result;
      try {
        if (quotationId) {
          // Convert quotation to bill
          result = await salesService.convertQuotationToBill(quotationId, request);
          setQuotationId(null);
          enqueueSnackbar('Quotation converted to bill successfully!', { variant: 'success' });
          setIsQuotationMode(false);
        } else if (editOrderId) {
          // Update existing sales order
          result = await salesService.updateSalesOrder(editOrderId, request);
          enqueueSnackbar('Order updated successfully!', { variant: 'success' });
          setEditOrderId(null);
          setIsQuotationMode(false);
        } else {
          // Create new sales order
          result = await salesService.createSalesOrder(request);
          const successMsg = isQuotationMode ? 'Quotation created successfully!' : 'Sale completed successfully!';
          enqueueSnackbar(successMsg, { variant: 'success' });
          if (isQuotationMode) setIsQuotationMode(false);
        }
      } catch (apiErr) {
        // Desktop offline fallback: save to local SQLite when API fails (network down)
        if (window.electronAPI && !quotationId && !editOrderId && !isQuotationMode) {
          try {
            const offlineResult = await window.electronAPI.saveOfflineOrder(request);
            enqueueSnackbar(`Offline: Bill saved (${offlineResult.localInvoiceNo}). Will sync when online.`, { variant: 'success' });
            setPaymentDialogOpen(false);
            const fakeOrder = { id: offlineResult.localId, orderNumber: offlineResult.localInvoiceNo };
            setLastCreatedOrder(fakeOrder);
            const user = JSON.parse(localStorage.getItem('user')) || {};
            const pdfBytes = generateOfflineReceiptPdf({
              cartItems: validItems,
              customer,
              totals,
              localInvoiceNo: offlineResult.localInvoiceNo,
              paymentMethod: backendPaymentMethod || paymentMethod,
              notes: notes || '',
              company: user?.company || {}
            });
            if (window.electronAPI.printPdf) {
              if (directPrint) {
                const defaultPrinter = await window.electronAPI.getDefaultPrinter();
                await window.electronAPI.printPdf(Array.from(pdfBytes), {
                  deviceName: defaultPrinter || undefined,
                  title: `POS Receipt ${offlineResult.localInvoiceNo}`
                });
              } else if (window.confirm('Print offline receipt?')) {
                const defaultPrinter = await window.electronAPI.getDefaultPrinter();
                await window.electronAPI.printPdf(Array.from(pdfBytes), {
                  deviceName: defaultPrinter || undefined,
                  title: `POS Receipt ${offlineResult.localInvoiceNo}`
                });
              }
            }
            clearAll();
            return;
          } catch (offlineErr) {
            console.error('Offline save failed', offlineErr);
          }
        }
        throw apiErr;
      }

      setPaymentDialogOpen(false);
      setLastCreatedOrder(result);

      if (directPrint) {
        handleDirectPrint(result);
      } else if (previewPrint && enableWhatsappShare) {
        setPrintChoiceDialogOpen(true);
      } else if (previewPrint) {
        setPreviewPrintDialogOpen(true);
      } else if (enableWhatsappShare) {
        setWhatsappDialogOpen(true);
      } else {
        const wantsToPrint = window.confirm('Do you want to print the receipt?');
        if (wantsToPrint) {
          handleDirectPrint(result);
        } else {
          clearAll();
        }
      }
    } catch (e) {
      console.error('Save sale failed', e);
      let msg = e?.response?.data?.message || e?.message || 'Failed to save sale';
      const lower = String(msg || '').toLowerCase();
      if (lower.includes('day is not opened') || lower.includes('day not opened') || lower.includes('already closed')) {
        msg = 'Day is not open. Please open the day before saving the bill.';
      }
      alert(msg);
    }
  }, [cart, calculateTotals, paymentMethod, customer, notes, discount, receivedAmount, selectedSalesmanId, redeemedPoints, enqueueSnackbar, clearAll, enableWhatsappShare, directPrint, previewPrint, isQuotationMode, quotationId, editOrderId, nonGstMode]);

  // Handle direct print - opens print dialog immediately
  const handleDirectPrint = async (order) => {
    if (!order?.id) {
      clearAll();
      return;
    }

    try {
      // Generate receipt
      const receiptBlob = await salesService.generateReceipt(order.id, 'CUSTOMER', null);

      if (window.electronAPI) {
        // --- Electron Silent Print Path ---
        try {
          const arrayBuffer = await receiptBlob.arrayBuffer();
          const data = Array.from(new Uint8Array(arrayBuffer));
          const defaultPrinter = await window.electronAPI.getDefaultPrinter();

          await window.electronAPI.printPdf(data, {
            deviceName: defaultPrinter || undefined,
            title: `POS Receipt ${order.orderNumber || order.id}`
          });

          clearAll();
          return; // Success, skip browser print
        } catch (electronErr) {
          console.error('Electron silent print failed, falling back to window.print():', electronErr);
          // Fallback to normal behavior below
        }
      }

      // --- Web/Browser Normal Path ---
      const blob = new Blob([receiptBlob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Open in new window for printing
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }

      // Clean up after a delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        clearAll();
      }, 1000);
    } catch (error) {
      console.error('Error printing receipt:', error);
      enqueueSnackbar('Failed to print receipt', { variant: 'error' });
      clearAll();
    }
  };

  // Load PDF preview when preview dialog opens
  useEffect(() => {
    if (previewPrintDialogOpen && lastCreatedOrder?.id) {
      loadPreviewPdf();
    }

    return () => {
      // Cleanup PDF URL when dialog closes
      if (previewPdfUrl) {
        window.URL.revokeObjectURL(previewPdfUrl);
        setPreviewPdfUrl(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewPrintDialogOpen, lastCreatedOrder?.id]);

  const loadPreviewPdf = async () => {
    if (!lastCreatedOrder?.id) return;

    try {
      setLoadingPreview(true);
      // Generate receipt PDF
      const receiptBlob = await salesService.generateReceipt(lastCreatedOrder.id, 'CUSTOMER', null);
      const blob = new Blob([receiptBlob], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPreviewPdfUrl(url);
    } catch (error) {
      console.error('Error loading preview PDF:', error);
      enqueueSnackbar('Failed to load bill preview', { variant: 'error' });
    } finally {
      setLoadingPreview(false);
    }
  };

  // Handle preview print - prints the previewed PDF
  const handlePreviewPrint = async () => {
    if (!previewPdfUrl) {
      enqueueSnackbar('Preview not loaded', { variant: 'error' });
      return;
    }

    try {
      // Open in new window for printing
      const printWindow = window.open(previewPdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }

      setPreviewPrintDialogOpen(false);
      setTimeout(() => {
        if (previewPdfUrl) {
          window.URL.revokeObjectURL(previewPdfUrl);
          setPreviewPdfUrl(null);
        }
        clearAll();
      }, 1000);
    } catch (error) {
      console.error('Error printing receipt:', error);
      enqueueSnackbar('Failed to print receipt', { variant: 'error' });
    }
  };

  // Handle preview dialog close
  const handlePreviewDialogClose = () => {
    setPreviewPrintDialogOpen(false);
    if (previewPdfUrl) {
      window.URL.revokeObjectURL(previewPdfUrl);
      setPreviewPdfUrl(null);
    }
    clearAll();
  };

  // Handle WhatsApp share confirmation
  const handleWhatsappShareConfirm = async () => {
    if (!lastCreatedOrder?.id) {
      setWhatsappDialogOpen(false);
      clearAll();
      return;
    }

    try {
      await shareBillToWhatsApp(
        lastCreatedOrder.id,
        customer,
        lastCreatedOrder.orderNumber || lastCreatedOrder.id
      );
    } catch (error) {
      console.error('Error sharing to WhatsApp:', error);
      alert('Failed to share bill to WhatsApp');
    } finally {
      setWhatsappDialogOpen(false);
      clearAll();
    }
  };

  // Handle WhatsApp share cancel
  const handleWhatsappShareCancel = () => {
    setWhatsappDialogOpen(false);
    clearAll();
  };

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      // F1: Add Customer
      if (e.key === 'F1') {
        e.preventDefault();
        setCustomerDialogOpen(true);
        return;
      }

      // F2: Split Bill
      if (e.key === 'F2') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && item.quantity > 0);
        if (validItems.length > 0) {
          setSplitBillDialogOpen(true);
        } else {
          enqueueSnackbar('Add items first', { variant: 'warning' });
        }
        return;
      }

      // F3: Direct Card Bill (Replaces Name Search)
      if (e.key === 'F3') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && item.quantity > 0);
        if (validItems.length > 0) {
          handlePayment('CREDIT_CARD');
        } else {
          enqueueSnackbar('Add items first', { variant: 'warning' });
        }
        return;
      }

      // F4: Direct UPI Bill
      if (e.key === 'F4') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && item.quantity > 0);
        if (validItems.length > 0) {
          handlePayment('PAYNOW');
        } else {
          enqueueSnackbar('Add items first', { variant: 'warning' });
        }
        return;
      }

      // F5: Direct Credit Bill
      if (e.key === 'F5') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && item.quantity > 0);
        if (validItems.length > 0) {
          handlePayment('CREDIT');
        } else {
          enqueueSnackbar('Add items first', { variant: 'warning' });
        }
        return;
      }

      // Space: Direct Cash Bill (Only when not in an input)
      if (e.code === 'Space') {
        const activeElement = document.activeElement;
        const isInput = activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.isContentEditable ||
          activeElement.getAttribute('role') === 'textbox' ||
          activeElement.getAttribute('role') === 'combobox';

        if (!isInput) {
          e.preventDefault();
          const validItems = cart.filter(item => item.product && item.quantity > 0);
          if (validItems.length > 0) {
            handlePayment('CASH');
          } else {
            enqueueSnackbar('Add items first', { variant: 'warning' });
          }
          return;
        }
      }

      // F6: Tender/Payment
      if (e.key === 'F6') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && item.quantity > 0);
        if (validItems.length > 0) {
          setPaymentDialogOpen(true);
        }
        return;
      }

      // F7: Clear Screen
      if (e.key === 'F7') {
        e.preventDefault();
        clearAll();
        return;
      }

      // F11: Select Customer
      if (e.key === 'F11') {
        e.preventDefault();
        setCustomerDialogOpen(true);
        return;
      }

      // Delete key: Remove row
      if (e.key === 'Delete') {
        const activeElement = document.activeElement;
        const isInput = activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable;

        if (!isInput && selectedRowIndex !== null && cart[selectedRowIndex]) {
          setRowToDelete(selectedRowIndex);
          setDeleteConfirmOpen(true);
        }
      }

      // P key: Show purchase price of selected row's product
      if (e.key === 'p' || e.key === 'P') {
        const activeElement = document.activeElement;
        const isInput = activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable;

        if (!isInput && selectedRowIndex !== null && cart[selectedRowIndex]?.product) {
          e.preventDefault();
          const item = cart[selectedRowIndex];
          const purchasePrice = item.product?.purchasePrice ?? item.product?.costPrice ?? 0;
          setPurchasePriceDialogData({
            productName: item.product?.name || item.productName || 'Product',
            purchasePrice: Number(purchasePrice) || 0
          });
          setPurchasePriceDialogOpen(true);
        }
      }

      // Ctrl+S: Quick save (respects selected payment method; Split Bill opens its dialog)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        const validItems = cart.filter(item => item.product && item.quantity > 0);
        if (validItems.length > 0) {
          if (paymentMethod === 'SPLIT_BILL') {
            setSplitBillDialogOpen(true);
          } else {
            handlePayment();
          }
        }
      }

      // Ctrl+Q: Toggle Non-GST mode (tax-included rate in Sale Rate, GST shows 0)
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'q') {
        e.preventDefault();
        setNonGstMode((prev) => {
          const next = !prev;
          setCart((c) =>
            c.map((it) => {
              if (!it.product) return it;
              const taxRate = (it.product?.tax?.rate || 0) / 100;
              if (next) {
                const basePrice = it.price || 0;
                const taxInclusive = basePrice * (1 + taxRate);
                return { ...it, price: Math.round(taxInclusive * 100) / 100 };
              } else {
                const inclusivePrice = it.price || 0;
                const basePrice = taxRate > 0 ? inclusivePrice / (1 + taxRate) : inclusivePrice;
                return { ...it, price: Math.round(basePrice * 100) / 100 };
              }
            })
          );
          return next;
        });
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [cart, handlePayment, clearAll, selectedRowIndex, deleteRow, deleteConfirmOpen, paymentMethod, nonGstMode]);

  // Load quotation or existing order when navigating from View Orders
  useEffect(() => {
    const loadQuotation = async () => {
      try {
        const quotationDataStr = localStorage.getItem('quotationToLoad');
        if (!quotationDataStr) return;

        const quotationData = JSON.parse(quotationDataStr);

        // Clear the quotation data from localStorage
        localStorage.removeItem('quotationToLoad');

        // If quotation doesn't have items, fetch them
        let quotation = quotationData;
        if (!quotation.items || quotation.items.length === 0) {
          quotation = await salesService.getSalesOrderById(quotation.id);
        }

        // Set customer
        if (quotation.customer) {
          setCustomer(quotation.customer);
        }

        // Load items into cart
        if (quotation.items && quotation.items.length > 0) {
          const cartItems = quotation.items.map((item, index) => {
            // Build product object with tax information
            const product = {
              ...(item.product || {}),
              id: item.productId || item.product?.id,
              name: item.productName || item.product?.name || 'Unknown Product',
              salePrice: item.unitPrice,
              sellingPrice: item.unitPrice,
              tax: item.tax || item.product?.tax || null
            };

            return {
              id: index + 1,
              itemCode: product.sku || product.barcode || '',
              productName: product.name,
              quantity: item.quantity,
              price: item.unitPrice,
              discount: item.discount || 0,
              discountPercentage: item.discountPercentage || 0,
              discountAmount: item.discountAmount || item.discount || 0,
              product: product,
              selectedUnit: item.unit || null
            };
          });

          // Add one empty row at the end
          cartItems.push({
            id: cartItems.length + 1,
            itemCode: '',
            productName: '',
            quantity: 1,
            price: 0,
            discount: 0,
            discountPercentage: 0,
            discountAmount: 0,
            product: null,
            selectedUnit: null
          });

          setCart(cartItems);

          // Set discount if present
          if (quotation.discount) {
            setDiscount(Number(quotation.discount));
          }
        }

        // Store quotation ID for reference (will be used when saving)
        setQuotationId(quotation.id);

        // Set quotation mode to false (we're converting to bill)
        setIsQuotationMode(false);

        // Restore Non-GST mode if quotation was non-GST
        setNonGstMode(!!quotation.nonGst);

        enqueueSnackbar('Quotation loaded. You can edit items and proceed to bill.', {
          variant: 'info',
          autoHideDuration: 4000
        });
      } catch (error) {
        console.error('Error loading quotation:', error);
        enqueueSnackbar('Failed to load quotation data', { variant: 'error' });
      }
    };

    const loadOrderForEdit = async () => {
      try {
        const orderIdStr = localStorage.getItem('orderToEditId');
        if (!orderIdStr) return;

        const orderId = Number(orderIdStr);
        if (!orderId) return;

        // Clear the value immediately to avoid re-loading on refresh
        localStorage.removeItem('orderToEditId');

        const order = await salesService.getSalesOrderById(orderId);

        // Set customer
        if (order.customer) {
          setCustomer(order.customer);
        }

        // Load items into cart
        if (order.items && order.items.length > 0) {
          const cartItems = order.items.map((item, index) => {
            // Build product object with tax information
            const product = {
              ...(item.product || {}),
              id: item.productId || item.product?.id,
              name: item.productName || item.product?.name || 'Unknown Product',
              salePrice: item.unitPrice,
              sellingPrice: item.unitPrice,
              tax: item.tax || item.product?.tax || null
            };

            return {
              id: index + 1,
              itemCode: product.sku || product.barcode || '',
              productName: product.name,
              quantity: item.quantity,
              price: item.unitPrice,
              discount: item.discount || 0,
              discountPercentage: item.discountPercentage || 0,
              discountAmount: item.discountAmount || item.discount || 0,
              product: product,
              selectedUnit: item.unit || null
            };
          });

          // Add one empty row at the end
          cartItems.push({
            id: cartItems.length + 1,
            itemCode: '',
            productName: '',
            quantity: 1,
            price: 0,
            discount: 0,
            discountPercentage: 0,
            discountAmount: 0,
            product: null,
            selectedUnit: null
          });

          setCart(cartItems);

          if (order.discount) {
            setDiscount(Number(order.discount));
          }
        }

        // Store order ID for reference when saving
        setEditOrderId(order.id);

        // Ensure quotation mode is off while editing a normal bill
        setIsQuotationMode(false);

        // Restore Non-GST mode if order was non-GST
        setNonGstMode(!!order.nonGst);

        enqueueSnackbar('Order loaded for editing. You can adjust items and save.', {
          variant: 'info',
          autoHideDuration: 4000
        });
      } catch (error) {
        console.error('Error loading order for edit:', error);
        enqueueSnackbar('Failed to load order for editing', { variant: 'error' });
      }
    };

    loadQuotation();
    loadOrderForEdit();
  }, [enqueueSnackbar]); // Run only once on mount

  // Initial load
  useEffect(() => {
    loadAllProducts();
    loadCustomers();
    loadUnits();
    loadRecentOrders();
    // Removed loadLocations() - location field removed for admin users
    setTimeout(() => {
      if (itemCodeRefs.current[0]) {
        itemCodeRefs.current[0].focus();
      }
    }, 500);
  }, [loadAllProducts, loadCustomers, loadUnits, loadRecentOrders]);

  // Calculate totals
  const totals = calculateTotals();
  const itemCount = cart.filter(item => item.product).length;
  // const totalQty = cart.reduce((sum, item) => item.product ? sum + item.quantity : sum, 0); // Unused variable

  // Non-GST mode: completely different color scheme (orange/amber) vs normal (blue)
  const headerBg = nonGstMode ? '#d84315' : '#154c79';
  const tableHeaderBg = nonGstMode ? '#e65100' : '#154c79';
  const pageBg = nonGstMode ? '#fff8e1' : '#f1f1f1';
  const chipItemsBg = nonGstMode ? '#ffe0b2' : '#e0f2f1';
  const chipItemsColor = nonGstMode ? '#bf360c' : '#00695c';
  const chipQtyBg = nonGstMode ? '#ffcc80' : '#e3f2fd';
  const chipQtyColor = nonGstMode ? '#e65100' : '#1565c0';

  return (
    <Box sx={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: pageBg,
      fontFamily: '"Roboto", sans-serif',
      transition: 'background-color 0.3s ease'
    }}>
      {/* 1. Header Section */}
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1,
          backgroundColor: headerBg,
          color: 'white',
          borderRadius: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', letterSpacing: 1 }}>
            POS
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              src={user.logoUrl ? `${user.logoUrl}${user.logoUrl.includes('?') ? '&' : '?'}_t=${Date.now()}` : ''}
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'white',
                border: '1px solid rgba(255,255,255,0.2)'
              }}
            >
              {!user.logoUrl && <BusinessIcon color="primary" sx={{ fontSize: 18 }} />}
            </Avatar>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ fontWeight: 'bold' }}
            >
              {user.companyName || 'Tiya Inventory'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          {/* Quotation Mode Toggle in Top Bar */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: isQuotationMode ? 'rgba(255, 152, 0, 0.2)' : 'transparent',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            border: isQuotationMode ? '1px solid #ff9800' : 'none'
          }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isQuotationMode}
                  onChange={(e) => setIsQuotationMode(e.target.checked)}
                  color="warning"
                  size="small"
                  sx={{
                    '& .MuiSwitch-track': {
                      backgroundColor: isQuotationMode ? '#ff9800' : '#ccc'
                    }
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DescriptionIcon fontSize="small" sx={{ color: isQuotationMode ? '#ff9800' : 'white' }} />
                  <Typography variant="body2" sx={{ color: 'white', fontWeight: isQuotationMode ? 'bold' : 'normal' }}>
                    Quotation Bill
                  </Typography>
                </Box>
              }
              sx={{ m: 0 }}
            />
          </Box>

          {/* Hold Bills Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setViewHoldBillsModalOpen(true)}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                height: 28,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'white' }
              }}
              startIcon={<ListIcon sx={{ fontSize: '1.2rem !important' }} />}
            >
              View Hold Bills
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setHoldBillModalOpen(true)}
              disabled={cart.length === 0 || !cart[0].product}
              sx={{
                borderColor: 'white',
                color: 'white',
                textTransform: 'none',
                height: 28,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', borderColor: 'white' },
                '&.Mui-disabled': { borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.3)' }
              }}
              startIcon={<SaveIcon sx={{ fontSize: '1.2rem !important' }} />}
            >
              Hold Bill
            </Button>
          </Box>

          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Bill Date: <span style={{ fontWeight: 'normal' }}>{new Date().toLocaleDateString('en-GB')}</span>
            <span style={{ fontWeight: 'normal', marginLeft: '12px' }}>{currentTime.toLocaleTimeString('en-US', { hour12: true })}</span>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/sales/view-orders')}
              sx={{ backgroundColor: 'white', color: headerBg, textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#f5f5f5' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>←</span>}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={clearAll}
              sx={{ borderColor: '#ef5350', color: '#ef5350', backgroundColor: 'white', textTransform: 'none', height: 28, '&:hover': { backgroundColor: '#ffebee', borderColor: '#ef5350' } }}
              startIcon={<span style={{ fontSize: '1.2em', lineHeight: 0 }}>⟲</span>}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* 2. Top Info Section - flex layout so controls fill space evenly */}
      <Paper elevation={1} sx={{ p: 1.5, m: 1, borderRadius: 1, backgroundColor: nonGstMode ? '#fff3e0' : 'white' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, width: '100%' }}>
          {/* Customer - equal flex */}
          <Box sx={{ flex: '1 1 160px', minWidth: 160, maxWidth: 320 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555' }}>Customer</Typography>
              {customer && customer.id !== 1 && (
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 'bold' }}>
                    Points: {customer.loyaltyPoints ?? 0}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'red', fontWeight: 'bold' }}>
                    Due Balance: {getCustomerCreditBalance(customer).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
            <Autocomplete
              fullWidth
              size="small"
              options={[{ id: 'ADD_NEW', name: '+ Add New Customer', phone: '' }, ...customers]}
              getOptionLabel={(option) => option.name || ''}
              value={customer}
              onChange={(e, newValue) => {
                if (newValue && newValue.id === 'ADD_NEW') {
                  setAddCustomerDialogOpen(true);
                } else {
                  setCustomer(newValue);
                }
              }}
              filterOptions={(options, params) => {
                const filtered = options.filter((option) => {
                  if (option.id === 'ADD_NEW') return true; // Always show Add New
                  const searchText = params.inputValue.toLowerCase();
                  return (
                    (option.name && option.name.toLowerCase().includes(searchText)) ||
                    (option.phone && option.phone.toLowerCase().includes(searchText))
                  );
                });
                return filtered;
              }}
              renderInput={(params) => <TextField {...params} placeholder="Customer..." sx={{ '& .MuiInputBase-root': { fontSize: '14px', backgroundColor: 'white' } }} />}
              renderOption={(props, option) => (
                <li {...props} style={{
                  color: option.id === 'ADD_NEW' ? '#1565c0' : 'inherit',
                  fontWeight: option.id === 'ADD_NEW' ? 'bold' : 'normal',
                  borderBottom: option.id === 'ADD_NEW' ? '1px dashed #ccc' : 'none'
                }}>
                  {option.name} {option.phone ? `(${option.phone})` : ''}
                </li>
              )}
            />
          </Box>

          {/* Salesman - equal flex */}
          <Box sx={{ flex: '1 1 140px', minWidth: 140, maxWidth: 260 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Salesman</Typography>
            <Select
              fullWidth
              size="small"
              value={selectedSalesmanId || ''}
              onChange={(e) => setSelectedSalesmanId(e.target.value || '')}
              displayEmpty
              sx={{ backgroundColor: 'white', fontSize: '14px', height: '40px' }}
              renderValue={(v) => {
                if (!v) return 'None';
                const sm = salesmen.find(s => s.id === parseInt(v, 10));
                return sm ? `${sm.name}${sm.phone ? ` (${sm.phone})` : ''}` : 'None';
              }}
            >
              <MenuItem value="">None</MenuItem>
              {salesmen.map((sm) => (
                <MenuItem key={sm.id} value={sm.id}>{sm.name}{sm.phone ? ` (${sm.phone})` : ''}</MenuItem>
              ))}
            </Select>
          </Box>

          {/* Payment Mode - equal flex */}
          <Box sx={{ flex: '1 1 120px', minWidth: 120, maxWidth: 200 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#555', display: 'block', mb: 0.5 }}>Payment Mode</Typography>
            <Select
              fullWidth
              size="small"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              sx={{ backgroundColor: 'white', fontSize: '14px', height: '40px' }}
            >
              <MenuItem value="CASH">💵 Cash</MenuItem>
              <MenuItem value="CREDIT_CARD">💳 Card</MenuItem>
              <MenuItem value="PAYNOW">📱 UPI</MenuItem>
              <MenuItem value="CREDIT">📝 Credit</MenuItem>
              <MenuItem value="SPLIT_BILL">💰 Split Bill</MenuItem>
            </Select>
          </Box>

          {/* Items / Qty Stats and Price Mode (Retailer / Wholesale) */}
          <Box sx={{ flex: '1 1 140px', minWidth: 140, maxWidth: 220, display: 'flex', gap: 0.5, pb: 0.5 }}>
            <Chip
              label={`Items: ${cart.filter(c => c.product).length}`}
              sx={{ backgroundColor: chipItemsBg, color: chipItemsColor, fontWeight: 'bold', border: nonGstMode ? '1px solid #ffcc80' : '1px solid #b2dfdb', height: '40px', fontSize: '13px', flex: 1, minWidth: 0 }}
            />
            <Chip
              label={`Qty: ${cart.reduce((sum, item) => item.product ? sum + Number(item.quantity) : sum, 0)}`}
              sx={{ backgroundColor: chipQtyBg, color: chipQtyColor, fontWeight: 'bold', border: nonGstMode ? '1px solid #ffb74d' : '1px solid #bbdefb', height: '40px', fontSize: '13px', flex: 1, minWidth: 0 }}
            />
          </Box>
          <FormControl size="small" sx={{ minWidth: 130, pb: 0.5 }}>
            <InputLabel id="pos-price-mode-label">Rate</InputLabel>
            <Select
              labelId="pos-price-mode-label"
              value={priceMode}
              label="Rate"
              onChange={(e) => setPriceMode(e.target.value)}
              sx={{
                height: '40px',
                backgroundColor: priceMode === 'wholesale' ? '#fff3e0' : '#e8f5e9',
                fontWeight: 'bold',
                fontSize: '13px'
              }}
            >
              <MenuItem value="retailer">Retail</MenuItem>
              <MenuItem value="wholesale">Wholesale</MenuItem>
            </Select>
          </FormControl>

          {/* Total - always at the right corner (marginLeft: auto pushes it right) */}
          <Box sx={{ flex: '0 0 auto', marginLeft: 'auto', alignSelf: 'center', textAlign: 'right', minWidth: 140 }}>
            <Typography variant="h2" sx={{ fontWeight: 'bold', color: '#e74c3c', lineHeight: 1, fontSize: '3rem' }}>
              {totals.total.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* 3. Product Grid */}
      <Box sx={{ flexGrow: 1, px: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <TableContainer component={Paper} elevation={1} sx={{ height: '100%', borderRadius: 1, backgroundColor: nonGstMode ? '#fffde7' : 'white' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '4%' }}>S.No</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '15%', textAlign: 'left' }}>Item Code</StyledTableHeadCell>
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '30%', textAlign: 'left' }}>Product Name</StyledTableHeadCell>
                {showMrp && <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '9%', textAlign: 'right' }}>MRP</StyledTableHeadCell>}
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '8%', textAlign: 'center' }}>Qty</StyledTableHeadCell>
                {showUom && <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '10%', textAlign: 'center' }}>UOM</StyledTableHeadCell>}
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '12%', textAlign: 'right' }}>Sale Rate</StyledTableHeadCell>
                {showGst && <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '8%', textAlign: 'center' }}>GST(%)</StyledTableHeadCell>}
                {showDiscount && <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '15%', textAlign: 'center' }}>Discount (% / ₹)</StyledTableHeadCell>}
                <StyledTableHeadCell sx={{ backgroundColor: tableHeaderBg, color: 'white', width: '12%', textAlign: 'right' }}>Amount</StyledTableHeadCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cart.map((item, index) => {
                const isSearching = !item.product;
                const rowBg = isSearching ? '#fffde7' : (nonGstMode ? (index % 2 === 0 ? '#fff8e1' : '#ffecb3') : (index % 2 === 0 ? '#f8f9fb' : 'white'));

                return (
                  <TableRow
                    key={item.id}
                    onClick={() => setSelectedRowIndex(index)}
                    sx={{
                      backgroundColor: selectedRowIndex === index ? '#fff9c4' : rowBg,
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: selectedRowIndex === index ? '#fff9c4' : '#f5f5f5' }
                    }}
                  >
                    <StyledTableCell align="center">{index + 1}</StyledTableCell>

                    {/* Item Code (fixed column - always shown) */}
                    <StyledTableCell sx={{ p: 0.5 }}>
                      <Autocomplete
                        freeSolo
                        options={productOptions[index] || []}
                        getOptionLabel={(option) => {
                          if (typeof option === 'string') return option;
                          return option.sku || option.name || '';
                        }}
                        filterOptions={(x) => x}
                        inputValue={item.itemCode}
                        onInputChange={(e, value, reason) => {
                          if (reason === 'input') handleItemCodeInputChange(index, value);
                          else if (reason === 'clear') clearRowProduct(index);
                        }}
                        onChange={(e, value) => {
                          if (value && typeof value === 'object') handleProductSelect(index, value);
                          else if (value === null) clearRowProduct(index);
                        }}
                        autoHighlight
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={isSearching ? "scan or type..." : ""}
                            variant="standard"
                            InputProps={{ ...params.InputProps, disableUnderline: true }}
                            inputRef={(el) => { if (el) itemCodeRefs.current[index] = el; }}
                            onKeyDown={(e) => {
                              const options = productOptions[index] || [];
                              const hasOptions = options.length > 0;
                              if (e.key === 'Enter') {
                                // When dropdown has options, let Autocomplete handle Enter so it selects the *highlighted* option (matches mouse click).
                                if (hasOptions) {
                                  params.onKeyDown?.(e);
                                  return;
                                }
                                e.preventDefault();
                                const code = (item.itemCode || '').trim();
                                if (!code) {
                                  handleTabOrderEnter(index, 'itemCode');
                                  return;
                                }

                                // 1) If this code already exists in the cart (by SKU/barcode/code),
                                // reuse that product so quantity is increased instead of typing text.
                                const lowerCode = code.toLowerCase();
                                const existing = cart.find((c) =>
                                  c.product &&
                                  (
                                    (c.product.sku && c.product.sku.toLowerCase() === lowerCode) ||
                                    (c.product.productCode && c.product.productCode.toLowerCase() === lowerCode) ||
                                    (c.product.product_code && c.product.product_code.toLowerCase() === lowerCode) ||
                                    (c.product.barcode && c.product.barcode.toLowerCase() === lowerCode) ||
                                    (c.product.code && c.product.code.toLowerCase() === lowerCode)
                                  )
                                );

                                if (existing && existing.product) {
                                  // Use current row as the scanning row; handleProductSelect will
                                  // bump quantity on the existing row and clear this row.
                                  handleProductSelect(index, existing.product);
                                  return;
                                }

                                // 2) Otherwise, run the normal product search by code/barcode.
                                searchProduct(code, index);
                                return;
                              }
                              if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                // When dropdown is open, let Autocomplete handle arrows for list highlight.
                                if (hasOptions) {
                                  params.onKeyDown?.(e);
                                  return;
                                }
                                if (e.key === 'ArrowDown' && index < cart.length - 1) {
                                  e.preventDefault();
                                  itemCodeRefs.current[index + 1]?.focus();
                                } else if (e.key === 'ArrowUp' && index > 0) {
                                  e.preventDefault();
                                  itemCodeRefs.current[index - 1]?.focus();
                                }
                                return;
                              }
                              params.onKeyDown?.(e);
                            }}
                            sx={{ '& input': { fontSize: '15px', padding: '4px' } }}
                          />
                        )}
                        renderOption={(props, option) => {
                          // Show Product Name big, Item Code (SKU) smaller
                          return (
                            <li {...props}>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {option.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                  {option.sku || ''}
                                </Typography>
                              </Box>
                            </li>
                          );
                        }}
                      />
                    </StyledTableCell>

                    {/* Product Name (fixed column) */}
                    <StyledTableCell sx={{ p: 0.5 }}>
                      <Typography variant="body2" sx={{ padding: '4px', fontSize: '15px' }}>
                        {item.productName || '-'}
                      </Typography>
                    </StyledTableCell>

                    {/* MRP - optional column */}
                    {showMrp && (
                      <StyledTableCell align="right" sx={{ p: 0.5 }}>
                        <Typography variant="body2" sx={{ padding: '4px', fontSize: '15px' }}>
                          {item.product?.mrp != null ? Number(item.product.mrp).toFixed(2) : '-'}
                        </Typography>
                      </StyledTableCell>
                    )}

                    {/* Qty */}
                    {/* Qty */}
                    <StyledTableCell sx={{ p: 0.5 }}>
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleQuantityChange(index, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleTabOrderEnter(index, 'qty');
                          }
                        }}
                        inputRef={(el) => (qtyRefs.current[index] = el)}
                        disabled={!item.product}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          '& input': {
                            fontSize: '15px',
                            padding: '4px',
                            textAlign: 'center',
                            backgroundColor: item.product ? '#fff' : 'transparent',
                            border: item.product ? '1px solid #ddd' : 'none',
                            borderRadius: '4px',
                            '-moz-appearance': 'textfield',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            }
                          }
                        }}
                      />
                    </StyledTableCell>

                    {/* UOM - optional column */}
                    {showUom && (
                      <StyledTableCell align="center" sx={{ p: 0.5 }}>
                        {(() => {
                          if (!item.product) return '-';
                          const availableUnits = getAvailableUnits(item.product);
                          if (availableUnits.length > 1) {
                            const currentUnitId = item.selectedUnit?.id || (availableUnits.find(u => u.type === 'MAIN')?.id) || '';
                            return (
                              <Select
                                value={currentUnitId}
                                onChange={(e) => handleUOMChange(index, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setTimeout(() => focusRef('saleRate', index), 50);
                                  }
                                }}
                                inputRef={(el) => { if (el) uomRefs.current[index] = el; }}
                                variant="standard"
                                disableUnderline
                                sx={{ fontSize: '15px', textAlign: 'center', width: '100%', '& .MuiSelect-select': { py: 0, textAlign: 'center' } }}
                              >
                                {availableUnits.map((u) => (
                                  <MenuItem key={u.id} value={u.id} sx={{ fontSize: '15px' }}>{u.name}</MenuItem>
                                ))}
                              </Select>
                            );
                          }
                          return item.selectedUnit?.name || item.product?.unit?.name || item.product?.uom || '-';
                        })()}
                      </StyledTableCell>
                    )}

                    {/* Sale Rate (fixed column) */}
                    <StyledTableCell sx={{ p: 0.5 }}>
                      <TextField
                        type="number"
                        value={item.price}
                        onChange={(e) => handlePriceChange(index, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleTabOrderEnter(index, 'saleRate');
                          }
                        }}
                        inputRef={(el) => (priceRefs.current[index] = el)}
                        disabled={!item.product}
                        variant="standard"
                        InputProps={{ disableUnderline: true }}
                        sx={{
                          '& input': {
                            fontSize: '15px',
                            padding: '4px',
                            textAlign: 'right',
                            backgroundColor: item.product ? '#fff' : 'transparent',
                            border: item.product ? '1px solid #ddd' : 'none',
                            borderRadius: '4px',
                            '-moz-appearance': 'textfield',
                            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                              '-webkit-appearance': 'none',
                              margin: 0,
                            }
                          }
                        }}
                      />
                    </StyledTableCell>

                    {/* GST(%) - optional column */}
                    {showGst && (
                      <StyledTableCell align="center" sx={{ p: 0.5 }}>
                        <Typography variant="body2" sx={{ padding: '4px', fontSize: '15px' }}>
                          {nonGstMode ? '0.00' : (item.product?.tax?.rate != null ? Number(item.product.tax.rate).toFixed(2) : '0.00')}
                        </Typography>
                      </StyledTableCell>
                    )}

                    {/* Discount - optional column */}
                    {showDiscount && (
                      <StyledTableCell sx={{ p: 0.5 }}>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'center' }}>
                          <Tooltip title="Discount Percentage">
                            <TextField
                              size="small"
                              type="number"
                              variant="outlined"
                              value={item.discountPercentage === 0 ? '' : item.discountPercentage}
                              onChange={(e) => handleItemDiscountChange(index, e.target.value, 'percentage')}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleTabOrderEnter(index, 'discount');
                                }
                              }}
                              disabled={!item.product}
                              InputProps={{
                                endAdornment: <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: '13px' } }}>%</InputAdornment>
                              }}
                              placeholder="0"
                              sx={{
                                width: '75px',
                                backgroundColor: item.product ? 'white' : 'transparent',
                                '& .MuiInputBase-root': { fontSize: '14px', height: '32px' },
                                '& input': {
                                  textAlign: 'right',
                                  padding: '4px 8px',
                                  '-moz-appearance': 'textfield',
                                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                    '-webkit-appearance': 'none',
                                    margin: 0,
                                  }
                                }
                              }}
                            />
                          </Tooltip>
                          <Tooltip title="Discount Amount">
                            <TextField
                              size="small"
                              type="number"
                              variant="outlined"
                              value={item.discountAmount === 0 ? '' : item.discountAmount}
                              onChange={(e) => handleItemDiscountChange(index, e.target.value, 'amount')}
                              onFocus={(e) => e.target.select()}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleTabOrderEnter(index, 'discount');
                                }
                              }}
                              inputRef={(el) => (discountRefs.current[index] = el)}
                              disabled={!item.product}
                              InputProps={{
                                startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '13px' } }}>₹</InputAdornment>
                              }}
                              placeholder="0"
                              sx={{
                                width: '85px',
                                backgroundColor: item.product ? 'white' : 'transparent',
                                '& .MuiInputBase-root': { fontSize: '14px', height: '32px' },
                                '& input': {
                                  textAlign: 'right',
                                  padding: '4px 8px',
                                  '-moz-appearance': 'textfield',
                                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                                    '-webkit-appearance': 'none',
                                    margin: 0,
                                  }
                                }
                              }}
                            />
                          </Tooltip>
                        </Box>
                      </StyledTableCell>
                    )}

                    {/* Amount (after tax: same logic as calculateTotals so row amount matches Net Amount) */}
                    <StyledTableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {item.product
                        ? (() => {
                          const itemTotal = (item.price * item.quantity) - (Number(item.discount) || 0);
                          if (nonGstMode) return itemTotal.toFixed(2);
                          const taxRate = (item.product?.tax?.rate || 0) / 100;
                          const amountWithTax = itemTotal + (itemTotal * taxRate);
                          return amountWithTax.toFixed(2);
                        })()
                        : '0.00'}
                    </StyledTableCell>
                  </TableRow>
                );
              })}

              {/* Add New Row Hint */}
              <TableRow>
                <TableCell colSpan={6 + (showMrp ? 1 : 0) + (showUom ? 1 : 0) + (showGst ? 1 : 0) + (showDiscount ? 1 : 0)} align="center" sx={{ border: 'none', py: 1 }}>
                  <Typography variant="caption" sx={{ color: '#888' }}>Press Enter on the last row to add a new item</Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 4. Footer Section */}
      <Paper elevation={2} sx={{ p: 1, m: 1, mt: 0, borderRadius: 1 }}>
        <Grid container spacing={2} sx={{ height: '100%' }}>

          {/* Left: Recent Transactions (Invoice No, Customer, Total) */}
          <Grid item xs={12} md={6} sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <TableContainer component={Paper} elevation={0} sx={{ border: nonGstMode ? '1px solid #ffcc80' : '1px solid #eee', flexGrow: 1, overflow: 'auto', backgroundColor: nonGstMode ? '#fffde7' : 'white' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <StyledTableHeadCell sx={{ py: 1, fontSize: '14px', pl: 1, backgroundColor: tableHeaderBg, color: 'white' }} align="left">Invoice No</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ py: 1, fontSize: '14px', backgroundColor: tableHeaderBg, color: 'white' }} align="left">Customer</StyledTableHeadCell>
                    <StyledTableHeadCell sx={{ py: 1, fontSize: '14px', pr: 1, backgroundColor: tableHeaderBg, color: 'white' }} align="right">Total</StyledTableHeadCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentOrders.length > 0 ? (
                    recentOrders.map((order) => (
                      <TableRow key={order.id} hover>
                        <StyledTableCell sx={{ py: 1.5, fontSize: '14px', pl: 1 }}>
                          {order.orderNumber}
                        </StyledTableCell>
                        <StyledTableCell sx={{ py: 1.5, fontSize: '14px' }}>
                          {order.customer?.name || 'Walk-in'}
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ py: 1.5, fontSize: '14px', fontWeight: 'bold', pr: 1 }}>
                          {formatCurrency(order.total)}
                        </StyledTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3, color: '#999', fontSize: '14px' }}>
                        No recent transactions
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Center: Overall Discount Fields – always visible; page setting only hides discount column in data table */}
          <Grid item xs={12} md={2} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <TextField
              label="Overall Disc %"
              type="number"
              variant="outlined"
              size="small"
              fullWidth
              value={overallDiscountPercentage === 0 ? '' : overallDiscountPercentage}
              onChange={(e) => handleOverallDiscountChange(e.target.value, 'percentage')}
              onFocus={(e) => e.target.select()}
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                style: { fontSize: '14px' }
              }}
              InputLabelProps={{ style: { fontSize: '13px' } }}
              sx={{ backgroundColor: 'white' }}
            />
            <TextField
              label="Overall Amt"
              type="number"
              variant="outlined"
              size="small"
              fullWidth
              value={discount === 0 ? '' : discount}
              onChange={(e) => handleOverallDiscountChange(e.target.value, 'amount')}
              onFocus={(e) => e.target.select()}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                style: { fontSize: '14px' }
              }}
              InputLabelProps={{ style: { fontSize: '13px' } }}
              sx={{ backgroundColor: 'white' }}
            />
          </Grid>

          {/* Right: Totals & Actions */}
          <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between' }}>
            <Box sx={{ p: 0.5, px: 1, backgroundColor: nonGstMode ? '#ffecb3' : '#f9f9f9', borderRadius: 1, border: nonGstMode ? '1px solid #ffcc80' : '1px solid #eee', mb: 0.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: -0.2 }}>
                <Typography variant="body1" color="textSecondary" sx={{ fontSize: '12px' }}>Subtotal:</Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '12px' }}>{formatCurrency(totals.subtotal)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: -0.2 }}>
                <Typography variant="body1" color="textSecondary" sx={{ fontSize: '12px' }}>Tax:</Typography>
                <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '12px' }}>{formatCurrency(totals.taxAmount)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: -0.2 }}>
                <Typography variant="body1" color="textSecondary" sx={{ fontSize: '12px' }}>Discount:</Typography>
                <Typography variant="body1" color="error" fontWeight="bold" sx={{ fontSize: '12px' }}>-{formatCurrency(totals.discount)}</Typography>
              </Box>
              {roundOffEnabled && totals.roundOff && Math.abs(totals.roundOff) >= 0.005 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: -0.2 }}>
                  <Typography variant="body1" color="textSecondary" sx={{ fontSize: '12px' }}>Round off:</Typography>
                  <Typography variant="body1" fontWeight="bold" sx={{ fontSize: '12px' }}>
                    {totals.roundOff > 0 ? '+' : '-'}
                    {formatCurrency(Math.abs(totals.roundOff))}
                  </Typography>
                </Box>
              )}
              <Divider sx={{ my: 0.3 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: '14px' }}>Net Amount:</Typography>
                <Typography variant="h6" color="primary" fontWeight="bold" sx={{ fontSize: '16px' }}>{formatCurrency(totals.total)}</Typography>
              </Box>
            </Box>

            {/* Keyboard Shortcut Reference */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.4, mt: 0 }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 'bold', color: '#34495e', opacity: 0.9 }}>
                [Space] Cash | [F2] Split | [F3] Card | [F4] UPI | [F5] Credit
              </Typography>
            </Box>

            {/* Bill / Print Button */}
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                const validItems = cart.filter(item => item.product && item.quantity > 0);
                validItems.length > 0 ? setPaymentDialogOpen(true) : enqueueSnackbar('Add items first', { variant: 'warning' });
              }}
              sx={{
                py: 0.5,
                backgroundColor: '#27ae60',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '16px',
                textTransform: 'none',
                boxShadow: 2,
                '&:hover': { backgroundColor: '#219150' }
              }}
              startIcon={<span style={{ fontSize: '20px' }}>🖨️</span>}
            >
              BILL / PRINT
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Dialogs logic remains the same, just keeping them in flow */}
      {/* Search Customer Dialog */}
      <Dialog open={customerDialogOpen} onClose={() => setCustomerDialogOpen(false)}>
        <DialogTitle>Search Customer</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={customers}
            getOptionLabel={(option) => option.name || ''}
            onChange={(event, newValue) => {
              setCustomer(newValue);
              setCustomerDialogOpen(false);
            }}
            renderInput={(params) => <TextField {...params} label="Customer" autoFocus />}
          />
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerDialogOpen} onClose={() => setAddCustomerDialogOpen(false)}>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent>
          <CustomerForm
            onSuccess={handleCustomerFormSuccess}
            onCancel={() => setAddCustomerDialogOpen(false)}
            initialName={newCustomerName}
          />
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h4" align="center" color="primary" gutterBottom>
              {formatCurrency(totals.total)}
            </Typography>
            {/* Customer name and current loyalty points */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body1">
                <strong>Customer:</strong> {customer?.name || 'Walk-in Customer'}
              </Typography>
              {customer?.id && customer?.name && !customer?.name?.toLowerCase?.().includes('walk-in') && (
                <Typography variant="body1" color="primary">
                  <strong>Points:</strong> {customer?.loyaltyPoints ?? 0} pts
                </Typography>
              )}
            </Box>
            {customer?.id && loyaltySettings?.pointValueRupees != null && Number(loyaltySettings.pointValueRupees) > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="small"
                  onClick={() => {
                    const pts = customer?.loyaltyPoints ?? 0;
                    const pointVal = Number(loyaltySettings.pointValueRupees) || 1;
                    const totalVal = Number(totals.total) || 0;
                    const maxRedeem = Math.min(pts, Math.floor(totalVal / pointVal));
                    setRedeemedPoints(maxRedeem);
                  }}
                  disabled={!(customer?.loyaltyPoints > 0 && totals.total > 0)}
                >
                  Redeem
                </Button>
                {redeemedPoints > 0 && (
                  <Button variant="text" size="small" onClick={() => setRedeemedPoints(0)}>Clear</Button>
                )}
                {redeemedPoints > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Redeemed: {redeemedPoints} pts = {formatCurrency(redeemedPoints * (Number(loyaltySettings.pointValueRupees) || 1))} · Amount to pay: {formatCurrency(Math.max(0, totals.total - redeemedPoints * (Number(loyaltySettings.pointValueRupees) || 1)))}
                  </Typography>
                )}
              </Box>
            )}
            <FormControl fullWidth>
              <InputLabel>Payment Method</InputLabel>
              <Select value={paymentMethod} label="Payment Method" onChange={(e) => setPaymentMethod(e.target.value)}>
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="CREDIT_CARD">Credit Card</MenuItem>
                <MenuItem value="PAYNOW">UPI</MenuItem>
                <MenuItem value="CREDIT">Credit / Pay Later</MenuItem>
                <MenuItem value="SPLIT_BILL">Split Bill</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Salesman (optional)</InputLabel>
              <Select
                value={selectedSalesmanId || ''}
                label="Salesman (optional)"
                onChange={(e) => setSelectedSalesmanId(e.target.value || '')}
              >
                <MenuItem value="">None</MenuItem>
                {salesmen.map((sm) => (
                  <MenuItem key={sm.id} value={sm.id}>{sm.name} {sm.phone ? `(${sm.phone})` : ''}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Received Amount"
              type="number"
              value={receivedAmount}
              onChange={(e) => setReceivedAmount(e.target.value)}
              onFocus={(e) => e.target.select()}
              fullWidth
              sx={{
                '& input': {
                  '-moz-appearance': 'textfield',
                  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                    '-webkit-appearance': 'none',
                    margin: 0,
                  }
                }
              }}
            />
            {paymentMethod === 'CASH' && (
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'grey.100', border: '1px solid', borderColor: 'grey.300' }}>
                {(() => {
                  const amountToPay = Math.max(0, (totals?.total ?? 0) - (redeemedPoints > 0 ? redeemedPoints * (Number(loyaltySettings?.pointValueRupees) || 1) : 0));
                  const received = parseFloat(receivedAmount) || 0;
                  const balance = amountToPay - received;
                  if (received > 0) {
                    if (balance > 0) {
                      return (
                        <Typography variant="body1" fontWeight="bold" color="error">
                          Balance to Pay: {formatCurrency(balance)}
                        </Typography>
                      );
                    }
                    if (balance < 0) {
                      return (
                        <Typography variant="body1" fontWeight="bold" color="success.main">
                          Change to Give: {formatCurrency(Math.abs(balance))}
                        </Typography>
                      );
                    }
                    return (
                      <Typography variant="body1" fontWeight="bold" color="success.main">
                        Exact amount received
                      </Typography>
                    );
                  }
                  return (
                    <Typography variant="body2" color="text.secondary">
                      Enter received amount to see balance / change
                    </Typography>
                  );
                })()}
              </Box>
            )}
            <TextField label="Notes" multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (paymentMethod === 'SPLIT_BILL') {
                setPaymentDialogOpen(false);
                setSplitBillDialogOpen(true);
              } else {
                handlePayment();
              }
            }}
            color="primary"
          >
            Complete Sale
          </Button>
        </DialogActions>
      </Dialog>

      {/* Split Bill Dialog */}
      <SplitBillDialog
        open={splitBillDialogOpen}
        onClose={() => setSplitBillDialogOpen(false)}
        totalAmount={Math.max(
          0,
          (totals?.total ?? 0) -
          (redeemedPoints > 0 ? redeemedPoints * (Number(loyaltySettings?.pointValueRupees) || 1) : 0)
        )}
        onConfirm={(splitData) => {
          setSplitBillDialogOpen(false);
          handlePayment(JSON.stringify(splitData));
        }}
        requireCustomerForCredit={true}
        hasCustomer={!!(customer?.id && customer?.name && !customer?.name?.toLowerCase?.().includes('walk-in'))}
      />

      {/* WhatsApp Dialog */}
      <Dialog open={whatsappDialogOpen} onClose={handleWhatsappShareCancel}>
        <DialogTitle>Share Bill on WhatsApp?</DialogTitle>
        <DialogContent>
          <Typography>Do you want to send this bill to the customer via WhatsApp?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWhatsappShareCancel}>No</Button>
          <Button onClick={handleWhatsappShareConfirm} variant="contained" color="success">Yes, Share</Button>
        </DialogActions>
      </Dialog>

      {/* Preview Print Dialog */}
      <Dialog
        open={previewPrintDialogOpen}
        onClose={handlePreviewDialogClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Bill Preview & Print</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handlePreviewDialogClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: 'calc(90vh - 120px)', overflow: 'hidden', display: 'flex', flexDirection: 'column', position: 'relative' }}>
          {loadingPreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '400px' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>Loading bill preview...</Typography>
            </Box>
          ) : previewPdfUrl ? (
            <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
              <iframe
                src={previewPdfUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  display: 'block'
                }}
                title="Bill Preview"
                onError={(e) => {
                  console.error('Error loading PDF in iframe:', e);
                  enqueueSnackbar('Failed to display PDF preview', { variant: 'error' });
                }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '400px' }}>
              <Typography variant="body2" color="text.secondary">Failed to load preview</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handlePreviewDialogClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (previewPdfUrl) {
                window.open(previewPdfUrl, '_blank');
              }
            }}
            variant="outlined"
            disabled={!previewPdfUrl || loadingPreview}
          >
            Open in New Tab
          </Button>
          <Button
            onClick={handlePreviewPrint}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!previewPdfUrl || loadingPreview}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      {/* Print Choice Dialog (Preview Print + WhatsApp) */}
      <Dialog open={printChoiceDialogOpen} onClose={() => { setPrintChoiceDialogOpen(false); clearAll(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Choose Action</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {lastCreatedOrder && (
              <Card variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Order #{lastCreatedOrder.orderNumber || lastCreatedOrder.id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer: {customer?.name || 'Walk-in Customer'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total: {formatCurrency(lastCreatedOrder.total || 0)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 2, fontWeight: 'medium' }}>
                    What would you like to do?
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setPrintChoiceDialogOpen(false);
              // Open preview dialog instead of directly printing
              setPreviewPrintDialogOpen(true);
            }}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            Preview & Print
          </Button>
          <Button
            onClick={async () => {
              setPrintChoiceDialogOpen(false);
              // Directly share to WhatsApp without confirmation dialog
              if (!lastCreatedOrder?.id) {
                clearAll();
                return;
              }
              try {
                await shareBillToWhatsApp(
                  lastCreatedOrder.id,
                  customer,
                  lastCreatedOrder.orderNumber || lastCreatedOrder.id
                );
                clearAll();
              } catch (error) {
                console.error('Error sharing to WhatsApp:', error);
                enqueueSnackbar('Failed to share bill to WhatsApp', { variant: 'error' });
                clearAll();
              }
            }}
            variant="contained"
            color="success"
            startIcon={<span>📱</span>}
          >
            WhatsApp Share
          </Button>
        </DialogActions>
      </Dialog>

      {/* Row Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            if (document.activeElement === deleteYesRef.current) {
              deleteNoRef.current?.focus();
            } else {
              deleteYesRef.current?.focus();
            }
          }
        }}
      >
        <DialogTitle>Delete Row</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this row ({selectedRowIndex !== null ? selectedRowIndex + 1 : ''})?</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            ref={deleteNoRef}
            variant="outlined"
          >
            No
          </Button>
          <Button
            onClick={() => {
              if (rowToDelete !== null) {
                deleteRow(rowToDelete);
                setSelectedRowIndex(null);
                setDeleteConfirmOpen(false);
                setRowToDelete(null);
              }
            }}
            ref={deleteYesRef}
            color="error"
            variant="contained"
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchase Price Dialog - P key on selected row */}
      <Dialog
        open={purchasePriceDialogOpen}
        onClose={() => { setPurchasePriceDialogOpen(false); setPurchasePriceDialogData(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Purchase Price</DialogTitle>
        <DialogContent>
          {purchasePriceDialogData && (
            <Box sx={{ pt: 0.5 }}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                {purchasePriceDialogData.productName}
              </Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">
                {formatCurrency(purchasePriceDialogData.purchasePrice)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPurchasePriceDialogOpen(false); setPurchasePriceDialogData(null); }} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hold Bill Modal */}
      <HoldBillModal
        open={holdBillModalOpen}
        onClose={() => setHoldBillModalOpen(false)}
        customer={customer}
        cartItems={cart.filter(item => item.product)} // Only pass items with actual products
        onSuccess={handleHoldBillSuccess}
      />

      {/* View Hold Bills Modal */}
      <ViewHoldBillsModal
        open={viewHoldBillsModalOpen}
        onClose={() => setViewHoldBillsModalOpen(false)}
        onProcessBill={handleProcessHoldBill}
      />
    </Box>
  );
};

export default POSKeyboard;
